import { useState, useMemo, useRef, useEffect, Fragment } from 'react';
import { createPortal } from 'react-dom';
import {
  Plus,
  Search,
  Receipt,
  Users,
  X,
  CheckCircle2,
  FileText,
  DollarSign,
  Trash2,
  Pencil,
  MoreVertical,
  CreditCard,
  Landmark,
  ChevronDown,
  ChevronRight,
  Building2,
  Star,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Ban,
  CheckCircle,
  Info,
  SearchX,
  ArrowDownToLine,
} from 'lucide-react';
import { EmptyState } from '@/components/shared/empty-state';
import { PageHeader } from '@/components/shared/page-header';
import { LocationBadge } from '@/components/shared/location-badge';
import { StatCard } from '@/components/shared/stat-card';
import { StatusBadge } from '@/components/shared/status-badge';
import {
  useRPCustomers,
  useRPInvoices,
  useRPPayments,
  useRPDashboard,
  useCreateRPCustomer,
  useCreateRPInvoice,
  useUpdateRPInvoiceStatus,
  useRecordRPPayment,
  useUpdateRPCustomer,
  useDeleteRPCustomer,
  useActivateRPCustomer,
  useCreateRPCustomerCard,
  useCreateRPCustomerBank,
  useRPCustomerCards,
  useRPCustomerBanks,
  useUpdateRPCustomerCard,
  useDeleteRPCustomerCard,
  useUpdateRPCustomerBank,
  useDeleteRPCustomerBank,
  useCustomerPrimaryMethods,
  useSetPrimaryCard,
  useSetPrimaryBank,
  useRPInvoice,
  useUpdateRPInvoice,
  useDeleteRPInvoice,
  useCreateRPPaymentLink,
  type RPCustomer,
  type RPCustomerCard,
  type RPCustomerBank,
  type RPInvoice,
  type RPInvoiceLine,
  type RPReceivedPayment,
} from '@/hooks/use-receive-payments';
import {
  RECURRING_FREQUENCIES,
  RECURRING_FREQUENCY_LABELS,
  type RecurringFrequency,
} from '@/hooks/use-recurring-sales';
import { formatCurrency, formatDateTime } from '@/lib/utils';
import { toast } from '@/stores/toast-store';
import { useCanOpenReports } from '@/hooks/use-reports-access';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useLocationStore } from '@/stores/location-store';
import { US_STATES } from '@/lib/constants';
import { PhoneInput } from '@/components/ui/phone-input';
import { DateInput } from '@/components/ui/date-input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { DataTablePagination } from '@/components/data-table/data-table-pagination';
import { validatePhone, sanitizePhone } from '@/lib/validators';
import { NewSaleTab } from './new-sale-tab';
import { PayByLinkTab } from './pay-by-link-tab';
import { RecurringTab } from './recurring-tab';
import {
  useSaleTransactions,
  useResubmitSaleTransaction,
  useCancelSaleTransaction,
  useRefundSaleTransaction,
  getSaleReversalActions,
  getRefundableBalance,
  hasRefundActivity,
  useSaleRefunds,
  validateRefundAmount,
  type SaleTransaction,
} from '@/hooks/use-sale-transactions';
import { apiErrorMessage } from '@/lib/api-error';
import { FundingSummaryTab } from './funding-summary-tab';
import { useLocationsWithExternalId } from '@/hooks/use-locations';
import { usePersistentTab } from '@/hooks/use-persistent-tab';
import { usePermissions } from '@/hooks/use-permissions';
import type { PermissionKey } from '@supernova/shared';

// ── Tab types ──
type Tab = 'new_sale' | 'pay_by_link' | 'recurring' | 'invoices' | 'customers' | 'payments' | 'funding_summary';
const VALID_TABS: readonly Tab[] = [
  'new_sale',
  'pay_by_link',
  'recurring',
  'invoices',
  'customers',
  'payments',
  'funding_summary',
];

// The key that opens each tab. New Sale has no View: taking a payment opens it.
const TAB_KEYS: Record<Tab, PermissionKey> = {
  new_sale: 'rp.sale.create',
  pay_by_link: 'rp.links.view',
  recurring: 'rp.recurring.view',
  invoices: 'rp.invoices.view',
  customers: 'rp.customers.view',
  payments: 'rp.transactions.view',
  funding_summary: 'rp.funding.view',
};

const PER_PAGE_OPTIONS = [10, 25, 50, 100];

// ── Main Collections Page ──

export function CollectionsPage() {
  const [activeTab, setActiveTab] = usePersistentTab<Tab>(VALID_TABS, 'new_sale');
  const [showNewInvoice, setShowNewInvoice] = useState(false);
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [showNewPaymentLink, setShowNewPaymentLink] = useState(false);

  const { selectedLocationId } = useLocationStore();
  const { data: dashboardData, isLoading: dashLoading } = useRPDashboard({
    locationId: selectedLocationId || undefined,
  });
  const dash = dashboardData?.data;

  // Each tab shows only with its key; a saved tab the user can't open moves
  // to the first one they can.
  const permissions = usePermissions();
  const tabs = (
    [
      { key: 'new_sale', label: 'New Sale' },
      { key: 'pay_by_link', label: 'Pay By Link' },
      { key: 'recurring', label: 'Recurring/Scheduled' },
      { key: 'invoices', label: 'Invoices' },
      { key: 'customers', label: 'Customers' },
      { key: 'payments', label: 'Transactions' },
      { key: 'funding_summary', label: 'Funding Summary' },
    ] as { key: Tab; label: string }[]
  ).filter((t) => permissions.has(TAB_KEYS[t.key]));
  const shownTab = permissions.has(TAB_KEYS[activeTab]) ? activeTab : null;
  const firstTab = tabs[0]?.key;
  useEffect(() => {
    if (!shownTab && firstTab) setActiveTab(firstTab);
  }, [shownTab, firstTab, setActiveTab]);

  return (
    <div className="min-w-0 space-y-6">
      <PageHeader title="Receive Payments" description="Manage invoices and customer payments">
        <LocationBadge />
      </PageHeader>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Receivable"
          value={dashLoading ? '...' : formatCurrency(dash?.invoices.totalReceivable ?? '0')}
          subtitle="Outstanding balance"
        />
        <StatCard
          title="Overdue"
          value={dashLoading ? '...' : formatCurrency(dash?.invoices.overdue ?? '0')}
          changeType="negative"
          subtitle="Past due date"
        />
        <StatCard
          title="Collected"
          value={dashLoading ? '...' : formatCurrency(dash?.invoices.collected ?? '0')}
          changeType="positive"
          subtitle="Payments received"
        />
        <StatCard
          title="Total Invoices"
          value={dashLoading ? '...' : String(dash?.invoices.total ?? 0)}
          subtitle={`${dash?.customers.active ?? 0} active customers`}
        />
      </div>

      {/* Tab bar */}
      <div className="border-b border-border overflow-x-auto scrollbar-hide">
        <div className="flex w-full min-w-0">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative shrink-0 px-4 py-2.5 text-xs sm:text-sm font-medium transition-colors whitespace-nowrap text-center flex items-center gap-2 ${
                activeTab === tab.key ? 'text-primary' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab.label}
              {activeTab === tab.key && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary" />}
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      {shownTab === 'new_sale' && <NewSaleTab onAddCustomer={() => setShowNewCustomer(true)} />}
      {shownTab === 'pay_by_link' && <PayByLinkTab onCreatePaymentLink={() => setShowNewPaymentLink(true)} />}
      {shownTab === 'recurring' && <RecurringTab />}
      {shownTab === 'invoices' && (
        <InvoicesTab
          onRecordPayment={() => setShowRecordPayment(true)}
          onCreateInvoice={() => setShowNewInvoice(true)}
        />
      )}
      {shownTab === 'customers' && <CustomersTab onAddCustomer={() => setShowNewCustomer(true)} />}
      {shownTab === 'payments' && <ReceivedPaymentsTab />}
      {shownTab === 'funding_summary' && <FundingSummaryTab />}

      {/* Modals */}
      {showNewInvoice && <NewInvoiceModal onClose={() => setShowNewInvoice(false)} />}
      {showNewCustomer && <NewCustomerModal onClose={() => setShowNewCustomer(false)} />}
      {showRecordPayment && <RecordPaymentModal onClose={() => setShowRecordPayment(false)} />}
      {showNewPaymentLink && <NewPaymentLinkModal onClose={() => setShowNewPaymentLink(false)} />}
    </div>
  );
}

// ── Invoices Tab ──

function InvoicesTab({
  onRecordPayment,
  onCreateInvoice,
}: {
  onRecordPayment: () => void;
  onCreateInvoice: () => void;
}) {
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const { selectedLocationId } = useLocationStore();
  const { data, isLoading } = useRPInvoices({
    status: statusFilter || undefined,
    locationId: selectedLocationId || undefined,
  });
  const updateStatus = useUpdateRPInvoiceStatus();
  const deleteInvoice = useDeleteRPInvoice();
  const [editingInvoice, setEditingInvoice] = useState<RPInvoice | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<{ id: string; number: string } | null>(null);
  const permissions = usePermissions();
  const canCreate = permissions.has('rp.invoices.create');
  const canRecordPayment = permissions.has('rp.invoices.payment');

  const invoices = data?.data ?? [];

  const filteredInvoices = useMemo(() => {
    if (!search.trim()) return invoices;
    const query = search.toLowerCase();
    return invoices.filter(
      (invoice) =>
        invoice.invoiceNumber.toLowerCase().includes(query) ||
        invoice.customer?.name?.toLowerCase().includes(query) ||
        invoice.customer?.company?.toLowerCase().includes(query),
    );
  }, [invoices, search]);

  const total = filteredInvoices.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(
    () => filteredInvoices.slice((page - 1) * perPage, page * perPage),
    [filteredInvoices, page, perPage],
  );

  const handleStatusChange = async (id: string, status: string) => {
    try {
      await updateStatus.mutateAsync({ id, status });
      toast.success(`Invoice status updated to ${status}`);
    } catch {
      toast.error('Failed to update invoice status');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
          >
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="pending">Pending</option>
            <option value="partial">Partial</option>
            <option value="paid">Paid</option>
            <option value="overdue">Overdue</option>
            <option value="cancelled">Cancelled</option>
            <option value="void">Void</option>
          </select>
          <div className="relative flex-1 lg:max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search by customer name or invoice number..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 lg:ml-auto">
          {canCreate && (
            <button
              onClick={onCreateInvoice}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 lg:flex-none"
            >
              <Plus className="h-4 w-4" />
              Create Invoice
            </button>
          )}
          {canRecordPayment && (
            <button
              onClick={onRecordPayment}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-primary px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/5 lg:flex-none"
            >
              <DollarSign className="h-4 w-4" />
              Record Payment
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading invoices...</div>
      ) : filteredInvoices.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
          {search ? (
            <EmptyState icon={SearchX} title="No matching invoices" description="Try a different search term." />
          ) : (
            <EmptyState
              icon={FileText}
              title="No invoices yet"
              description="Create your first invoice to start tracking collections."
            />
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-white overflow-x-auto">
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b border-border bg-muted/50 rounded-t-lg">
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground rounded-tl-lg">
                  Invoice #
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Customer</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Due Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground rounded-tr-lg">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((invoice) => (
                <tr key={invoice.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                  <td className="px-4 py-3">
                    <span className="font-mono text-xs font-medium text-primary">{invoice.invoiceNumber}</span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {invoice.customer?.name || 'Unknown'}
                    {invoice.customer?.company && (
                      <span className="ml-1 text-xs text-muted-foreground">({invoice.customer.company})</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-right">{formatCurrency(invoice.amount)}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {invoice.dueDate ? new Date(invoice.dueDate).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={invoice.status} />
                  </td>
                  <td className="px-4 py-3">
                    <InvoiceActions
                      invoice={invoice}
                      onStatusChange={handleStatusChange}
                      isUpdating={updateStatus.isPending}
                      onEdit={() => setEditingInvoice(invoice)}
                      onDelete={() => setConfirmDelete({ id: invoice.id, number: invoice.invoiceNumber })}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && total > 0 && (
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          perPageOptions={PER_PAGE_OPTIONS}
          onPerPageChange={(n) => {
            setPerPage(n);
            setPage(1);
          }}
        />
      )}

      {/* Edit Invoice Modal */}
      {editingInvoice && <EditInvoiceModal invoice={editingInvoice} onClose={() => setEditingInvoice(null)} />}

      {/* Delete Invoice Confirmation */}
      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Invoice"
        message={`Are you sure you want to delete invoice "${confirmDelete?.number}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleteInvoice.isPending}
        onConfirm={async () => {
          if (!confirmDelete) return;
          try {
            await deleteInvoice.mutateAsync(confirmDelete.id);
            toast.success(`Invoice "${confirmDelete.number}" deleted successfully`);
            setConfirmDelete(null);
          } catch {
            toast.error('Failed to delete invoice');
            setConfirmDelete(null);
          }
        }}
        onCancel={() => setConfirmDelete(null)}
      />
    </div>
  );
}

function InvoiceActions({
  invoice,
  onStatusChange,
  isUpdating,
  onEdit,
  onDelete,
}: {
  invoice: RPInvoice;
  onStatusChange: (id: string, status: string) => void;
  isUpdating: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const permissions = usePermissions();
  const canSend = permissions.has('rp.invoices.send');
  const canVoid = permissions.has('rp.invoices.void');
  const canEdit = permissions.has('rp.invoices.edit');
  const canDelete = permissions.has('rp.invoices.delete');
  const actions: { label: string; status: string; variant: string }[] = [];

  if (invoice.status === 'draft' && canSend) {
    actions.push({ label: 'Send', status: 'sent', variant: 'bg-blue-600 hover:bg-blue-700 text-white' });
  }
  if (invoice.status === 'sent' && canSend) {
    actions.push({
      label: 'Mark Pending',
      status: 'pending',
      variant: 'bg-yellow-100 hover:bg-yellow-200 text-yellow-800',
    });
  }
  if (['draft', 'sent', 'pending'].includes(invoice.status) && canVoid) {
    actions.push({ label: 'Void', status: 'void', variant: 'bg-gray-100 hover:bg-gray-200 text-gray-700' });
  }

  // Editing and deleting are for drafts only.
  const isDraft = invoice.status === 'draft' && (canEdit || canDelete);

  return (
    <div className="flex items-center gap-1.5">
      {actions.map((action) => (
        <button
          key={action.status}
          onClick={() => onStatusChange(invoice.id, action.status)}
          disabled={isUpdating}
          className={`inline-flex h-7 items-center rounded px-2.5 text-xs font-medium transition-colors disabled:opacity-50 ${action.variant}`}
        >
          {action.label}
        </button>
      ))}
      {isDraft && (
        <div className="relative">
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Draft invoice actions"
            title="Draft invoice actions"
            className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <MoreVertical className="h-4 w-4" />
          </button>
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div className="absolute right-0 top-8 z-50 w-36 rounded-lg border border-border bg-white py-1 shadow-lg">
                {canEdit && (
                  <button
                    onClick={() => {
                      onEdit();
                      setMenuOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                    Edit
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={() => {
                      onDelete();
                      setMenuOpen(false);
                    }}
                    className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Customers Tab ──

function CustomerExpandedRow({ customer }: { customer: RPCustomer }) {
  const { data: cardsData, isLoading: cardsLoading } = useRPCustomerCards(customer.id);
  const { data: banksData, isLoading: banksLoading } = useRPCustomerBanks(customer.id);
  const cards = cardsData?.data ?? [];
  const banks = banksData?.data ?? [];
  const deleteCard = useDeleteRPCustomerCard();
  const deleteBank = useDeleteRPCustomerBank();
  const { data: primaryData } = useCustomerPrimaryMethods(customer.id);
  const primaryMethods = primaryData?.data;
  const setPrimaryCard = useSetPrimaryCard();
  const setPrimaryBank = useSetPrimaryBank();
  // Adding and editing cards and banks and setting the primary need Edit;
  // removing them needs Delete.
  const permissions = usePermissions();
  const canEdit = permissions.has('rp.customers.edit');
  const canDelete = permissions.has('rp.customers.delete');

  const [editCard, setEditCard] = useState<RPCustomerCard | null>(null);
  const [editBank, setEditBank] = useState<RPCustomerBank | null>(null);
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [addBankOpen, setAddBankOpen] = useState(false);
  const [confirmDeleteCard, setConfirmDeleteCard] = useState<{ id: string; label: string } | null>(null);
  const [confirmDeleteBank, setConfirmDeleteBank] = useState<{ id: string; label: string } | null>(null);
  const [confirmPrimaryCard, setConfirmPrimaryCard] = useState<{ id: string; label: string } | null>(null);
  const [confirmPrimaryBank, setConfirmPrimaryBank] = useState<{ id: string; label: string } | null>(null);

  const currentPrimaryCard = cards.find((c) => c.id === primaryMethods?.primaryCardId);
  const currentPrimaryBank = banks.find((b) => b.id === primaryMethods?.primaryBankId);

  const handleDeleteCard = async () => {
    if (!confirmDeleteCard) return;
    try {
      await deleteCard.mutateAsync({ customerId: customer.id, id: confirmDeleteCard.id });
      toast.success('Card details deleted');
      setConfirmDeleteCard(null);
    } catch {
      toast.error('Failed to delete card');
      setConfirmDeleteCard(null);
    }
  };
  const handleDeleteBank = async () => {
    if (!confirmDeleteBank) return;
    try {
      await deleteBank.mutateAsync({ customerId: customer.id, id: confirmDeleteBank.id });
      toast.success('Bank details deleted');
      setConfirmDeleteBank(null);
    } catch {
      toast.error('Failed to delete bank details');
      setConfirmDeleteBank(null);
    }
  };

  const handleConfirmPrimaryCard = async () => {
    if (!confirmPrimaryCard) return;
    try {
      await setPrimaryCard.mutateAsync({ customerId: customer.id, cardId: confirmPrimaryCard.id });
      toast.success(`"${confirmPrimaryCard.label}" is now your primary card`);
      setConfirmPrimaryCard(null);
    } catch {
      toast.error('Failed to set primary card');
      setConfirmPrimaryCard(null);
    }
  };
  const handleConfirmPrimaryBank = async () => {
    if (!confirmPrimaryBank) return;
    try {
      await setPrimaryBank.mutateAsync({ customerId: customer.id, bankId: confirmPrimaryBank.id });
      toast.success(`"${confirmPrimaryBank.label}" is now your primary bank account`);
      setConfirmPrimaryBank(null);
    } catch {
      toast.error('Failed to set primary bank account');
      setConfirmPrimaryBank(null);
    }
  };

  const isLoading = cardsLoading || banksLoading;

  return (
    <tr>
      <td colSpan={6} className="p-0">
        <div className="bg-slate-50 px-6 py-4 border-t border-border">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading payment methods...</p>
          ) : cards.length === 0 && banks.length === 0 ? (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">No payment methods on file.</p>
              {canEdit && (
                <div className="flex gap-2">
                  <button
                    onClick={() => setAddCardOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                  >
                    <CreditCard className="h-3 w-3" /> Add Card
                  </button>
                  <button
                    onClick={() => setAddBankOpen(true)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                  >
                    <Landmark className="h-3 w-3" /> Add Bank
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Card Details Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <CreditCard className="h-3.5 w-3.5" /> Card Details
                  </h4>
                  {canEdit && (
                    <button
                      onClick={() => setAddCardOpen(true)}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-2 py-1 text-xs font-medium text-foreground hover:bg-muted"
                    >
                      <Plus className="h-3 w-3" /> Add Card
                    </button>
                  )}
                </div>
                {cards.length === 0 ? (
                  <p className="text-xs text-muted-foreground ml-5">No cards on file</p>
                ) : (
                  <div className="space-y-2">
                    {cards.map((card) => (
                      <div
                        key={card.id}
                        className="flex items-center justify-between rounded-lg border border-border bg-white px-4 py-2.5"
                      >
                        <div className="flex items-center gap-3">
                          <CreditCard className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              •••• {card.cardLastFour}
                              {card.cardHolderName && (
                                <span className="ml-2 text-muted-foreground font-normal">({card.cardHolderName})</span>
                              )}
                            </p>
                            <p className="text-xs text-muted-foreground">Exp: {card.expirationDate}</p>
                          </div>
                          {primaryMethods?.primaryCardId === card.id && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                              <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {canEdit && primaryMethods?.primaryCardId !== card.id && (
                            <button
                              onClick={() => setConfirmPrimaryCard({ id: card.id, label: `•••• ${card.cardLastFour}` })}
                              className="rounded-md p-1.5 text-muted-foreground hover:bg-amber-50 hover:text-amber-600"
                              title="Set as Primary"
                            >
                              <Star className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={() => setEditCard(card)}
                              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                              title="Edit"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() => setConfirmDeleteCard({ id: card.id, label: `•••• ${card.cardLastFour}` })}
                              className="rounded-md p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bank Details Section */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                    <Building2 className="h-3.5 w-3.5" /> Bank Details
                  </h4>
                  {canEdit && (
                    <button
                      onClick={() => setAddBankOpen(true)}
                      className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-2 py-1 text-xs font-medium text-foreground hover:bg-muted"
                    >
                      <Plus className="h-3 w-3" /> Add Bank
                    </button>
                  )}
                </div>
                {banks.length === 0 ? (
                  <p className="text-xs text-muted-foreground ml-5">No bank accounts on file</p>
                ) : (
                  <div className="space-y-2">
                    {banks.map((bank) => (
                      <div
                        key={bank.id}
                        className="flex items-center justify-between rounded-lg border border-border bg-white px-4 py-2.5"
                      >
                        <div className="flex items-center gap-3">
                          <Building2 className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm font-medium text-foreground">
                              {bank.firstName} {bank.lastName}
                              <span className="ml-2 text-xs text-muted-foreground capitalize">
                                ({bank.accountCategory} · {bank.accountType})
                              </span>
                            </p>
                            <p className="text-xs text-muted-foreground">
                              Routing: •••{bank.routingNumber.slice(-4)} · Acct: •••{bank.accountNumber.slice(-4)}
                              {bank.phone && <span className="ml-2">· {bank.phone}</span>}
                            </p>
                          </div>
                          {primaryMethods?.primaryBankId === bank.id && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                              <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                              Primary
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          {canEdit && primaryMethods?.primaryBankId !== bank.id && (
                            <button
                              onClick={() =>
                                setConfirmPrimaryBank({ id: bank.id, label: `${bank.firstName} ${bank.lastName}` })
                              }
                              className="rounded-md p-1.5 text-muted-foreground hover:bg-amber-50 hover:text-amber-600"
                              title="Set as Primary"
                            >
                              <Star className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {canEdit && (
                            <button
                              onClick={() => setEditBank(bank)}
                              className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
                              title="Edit"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                          )}
                          {canDelete && (
                            <button
                              onClick={() =>
                                setConfirmDeleteBank({ id: bank.id, label: `${bank.firstName} ${bank.lastName}` })
                              }
                              className="rounded-md p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Modals */}
        {addCardOpen && <AddCardDetailsModal customer={customer} onClose={() => setAddCardOpen(false)} />}
        {addBankOpen && <AddBankDetailsModal customer={customer} onClose={() => setAddBankOpen(false)} />}
        {editCard && <EditCardDetailsModal customer={customer} card={editCard} onClose={() => setEditCard(null)} />}
        {editBank && <EditBankDetailsModal customer={customer} bank={editBank} onClose={() => setEditBank(null)} />}

        <ConfirmDialog
          open={!!confirmDeleteCard}
          title="Delete Card"
          message={`Are you sure you want to delete card "${confirmDeleteCard?.label}"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          loading={deleteCard.isPending}
          onConfirm={handleDeleteCard}
          onCancel={() => setConfirmDeleteCard(null)}
        />
        <ConfirmDialog
          open={!!confirmDeleteBank}
          title="Delete Bank Details"
          message={`Are you sure you want to delete bank details for "${confirmDeleteBank?.label}"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="danger"
          loading={deleteBank.isPending}
          onConfirm={handleDeleteBank}
          onCancel={() => setConfirmDeleteBank(null)}
        />

        <ConfirmDialog
          open={!!confirmPrimaryCard}
          title="Set Primary Card"
          message={
            currentPrimaryCard && currentPrimaryCard.id !== confirmPrimaryCard?.id
              ? `You currently have "•••• ${currentPrimaryCard.cardLastFour}" set as the primary card. Are you sure you want to switch your primary card to "${confirmPrimaryCard?.label}"?`
              : `Are you sure you want to make "${confirmPrimaryCard?.label}" your primary card?`
          }
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          variant="warning"
          loading={setPrimaryCard.isPending}
          onConfirm={handleConfirmPrimaryCard}
          onCancel={() => setConfirmPrimaryCard(null)}
        />
        <ConfirmDialog
          open={!!confirmPrimaryBank}
          title="Set Primary Bank Account"
          message={
            currentPrimaryBank && currentPrimaryBank.id !== confirmPrimaryBank?.id
              ? `You currently have "${currentPrimaryBank.firstName} ${currentPrimaryBank.lastName}" set as the primary bank account. Are you sure you want to switch your primary bank account to "${confirmPrimaryBank?.label}"?`
              : `Are you sure you want to make "${confirmPrimaryBank?.label}" your primary bank account?`
          }
          confirmLabel="Confirm"
          cancelLabel="Cancel"
          variant="warning"
          loading={setPrimaryBank.isPending}
          onConfirm={handleConfirmPrimaryBank}
          onCancel={() => setConfirmPrimaryBank(null)}
        />
      </td>
    </tr>
  );
}

function CustomersTab({ onAddCustomer }: { onAddCustomer: () => void }) {
  const canAddCustomer = usePermissions().has('rp.customers.create');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const { data, isLoading } = useRPCustomers({ search: search || undefined });
  const customers = data?.data ?? [];

  const total = customers.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(() => customers.slice((page - 1) * perPage, page * perPage), [customers, page, perPage]);
  const deactivateCustomer = useDeleteRPCustomer();
  const activateCustomer = useActivateRPCustomer();
  const [editingCustomer, setEditingCustomer] = useState<RPCustomer | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ id: string; name: string; isActive: boolean } | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const handleConfirmToggle = async () => {
    if (!confirmDialog) return;
    try {
      if (confirmDialog.isActive) {
        await deactivateCustomer.mutateAsync(confirmDialog.id);
        toast.success(`Customer "${confirmDialog.name}" deactivated successfully`);
      } else {
        await activateCustomer.mutateAsync(confirmDialog.id);
        toast.success(`Customer "${confirmDialog.name}" activated successfully`);
      }
      setConfirmDialog(null);
    } catch {
      toast.error(confirmDialog.isActive ? 'Failed to deactivate customer' : 'Failed to activate customer');
      setConfirmDialog(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search customers..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
        {canAddCustomer && (
          <button
            onClick={onAddCustomer}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            <Plus className="h-4 w-4" />
            Add Customer
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading customers...</div>
      ) : customers.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
          <EmptyState
            icon={Users}
            title="No customers yet"
            description="Add a customer to send them invoices and track their payments."
          />
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="w-10 px-3 py-3"></th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Name</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Company</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Email</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Phone</th>
                <th className="px-4 py-3 text-left font-medium text-muted-foreground">Status</th>
                <th className="w-12 px-3 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((customer) => (
                <CustomerTableRow
                  key={customer.id}
                  customer={customer}
                  isExpanded={expandedId === customer.id}
                  onToggleExpand={() => setExpandedId(expandedId === customer.id ? null : customer.id)}
                  openMenuId={openMenuId}
                  setOpenMenuId={setOpenMenuId}
                  onEdit={() => {
                    setEditingCustomer(customer);
                    setOpenMenuId(null);
                  }}
                  onToggleStatus={() => {
                    setConfirmDialog({ id: customer.id, name: customer.name, isActive: customer.isActive });
                    setOpenMenuId(null);
                  }}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && total > 0 && (
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          perPageOptions={PER_PAGE_OPTIONS}
          onPerPageChange={(n) => {
            setPerPage(n);
            setPage(1);
          }}
        />
      )}

      {/* Edit Customer Modal */}
      {editingCustomer && <EditCustomerModal customer={editingCustomer} onClose={() => setEditingCustomer(null)} />}

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!confirmDialog}
        title={confirmDialog?.isActive ? 'Deactivate Customer' : 'Activate Customer'}
        message={
          confirmDialog?.isActive
            ? `Are you sure you want to deactivate "${confirmDialog?.name}"? The customer will be marked as inactive.`
            : `Are you sure you want to activate "${confirmDialog?.name}"? The customer will be marked as active.`
        }
        confirmLabel={confirmDialog?.isActive ? 'Deactivate' : 'Activate'}
        cancelLabel="Cancel"
        variant={confirmDialog?.isActive ? 'danger' : 'warning'}
        loading={deactivateCustomer.isPending || activateCustomer.isPending}
        onConfirm={handleConfirmToggle}
        onCancel={() => setConfirmDialog(null)}
      />
    </div>
  );
}

function CustomerTableRow({
  customer,
  isExpanded,
  onToggleExpand,
  openMenuId,
  setOpenMenuId,
  onEdit,
  onToggleStatus,
}: {
  customer: RPCustomer;
  isExpanded: boolean;
  onToggleExpand: () => void;
  openMenuId: string | null;
  setOpenMenuId: (id: string | null) => void;
  onEdit: () => void;
  onToggleStatus: () => void;
}) {
  return (
    <>
      <tr
        className={`border-b border-border hover:bg-muted/30 transition-colors cursor-pointer ${isExpanded ? 'bg-muted/20' : ''}`}
        onClick={onToggleExpand}
      >
        <td className="px-3 py-3">
          <span className="inline-flex rounded-md p-1 text-muted-foreground">
            {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          </span>
        </td>
        <td className="px-4 py-3">
          <span className="font-medium text-foreground">{customer.name}</span>
        </td>
        <td className="px-4 py-3 text-muted-foreground">{customer.company || '—'}</td>
        <td className="px-4 py-3 text-muted-foreground">{customer.email || '—'}</td>
        <td className="px-4 py-3 text-muted-foreground">{customer.phone || '—'}</td>
        <td className="px-4 py-3">
          <StatusBadge status={customer.isActive ? 'active' : 'inactive'} />
        </td>
        <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
          <CustomerActionsMenu
            isOpen={openMenuId === customer.id}
            onToggle={() => setOpenMenuId(openMenuId === customer.id ? null : customer.id)}
            onClose={() => setOpenMenuId(null)}
            isActive={customer.isActive}
            onEdit={onEdit}
            onToggleStatus={onToggleStatus}
          />
        </td>
      </tr>
      {isExpanded && <CustomerExpandedRow customer={customer} />}
    </>
  );
}

function CustomerActionsMenu({
  isOpen,
  onToggle,
  onClose,
  isActive,
  onEdit,
  onToggleStatus,
}: {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  isActive: boolean;
  onEdit: () => void;
  onToggleStatus: () => void;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  // Edit needs Edit; deactivating and reactivating need Delete.
  const permissions = usePermissions();
  const canEdit = permissions.has('rp.customers.edit');
  const canToggleStatus = permissions.has('rp.customers.delete');

  const handleToggle = () => {
    if (isOpen) {
      onToggle();
      return;
    }
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const menuWidth = 144;
      setCoords({ top: rect.bottom + 4, left: rect.right - menuWidth });
    }
    onToggle();
  };

  useEffect(() => {
    if (!isOpen) return;
    window.addEventListener('scroll', onClose, true);
    window.addEventListener('resize', onClose);
    return () => {
      window.removeEventListener('scroll', onClose, true);
      window.removeEventListener('resize', onClose);
    };
  }, [isOpen, onClose]);

  if (!canEdit && !canToggleStatus) return null;

  return (
    <>
      <button
        ref={triggerRef}
        onClick={handleToggle}
        aria-label="Customer actions"
        title="Customer actions"
        className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
      >
        <MoreVertical className="h-4 w-4" />
      </button>
      {isOpen &&
        coords &&
        createPortal(
          <>
            <div className="fixed inset-0 z-40" onClick={onClose} />
            <div
              style={{ top: coords.top, left: coords.left }}
              className="fixed z-50 w-36 rounded-lg border border-border bg-white py-1 shadow-lg"
            >
              {canEdit && (
                <button
                  onClick={onEdit}
                  className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors"
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit
                </button>
              )}
              {canToggleStatus && (
                <button
                  onClick={onToggleStatus}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors ${
                    isActive ? 'text-red-600 hover:bg-red-50' : 'text-green-600 hover:bg-green-50'
                  }`}
                >
                  {isActive ? (
                    <>
                      <Ban className="h-3.5 w-3.5" /> Deactivate
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-3.5 w-3.5" /> Activate
                    </>
                  )}
                </button>
              )}
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

// ── Edit Customer Modal ──

function EditCustomerModal({ customer, onClose }: { customer: RPCustomer; onClose: () => void }) {
  const updateCustomer = useUpdateRPCustomer();
  // Split existing name into first/last for editing
  const nameParts = (customer.name || '').trim().split(/\s+/);
  const [formData, setFormData] = useState({
    firstName: nameParts[0] || '',
    lastName: nameParts.slice(1).join(' ') || '',
    email: customer.email || '',
    phone: customer.phone || '',
    company: customer.company || '',
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const update = (field: string, value: string) => {
    if (field === 'firstName' || field === 'lastName') {
      value = value.replace(/[^a-zA-Z\s]/g, '');
    }
    if (field === 'phone') {
      value = sanitizePhone(value);
    }
    if (field === 'company') {
      value = value.replace(/[^a-zA-Z\s]/g, '');
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const inputClass = (field: string, errors: Record<string, string>) =>
    `mt-1 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${
      touched[field] && errors[field]
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-input focus:border-primary focus:ring-primary'
    }`;

  const fieldErrors: Record<string, string> = {};

  // First Name: mandatory, alphabets and spaces only, min 2 chars
  if (!formData.firstName.trim()) {
    fieldErrors.firstName = 'First name is required';
  } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName)) {
    fieldErrors.firstName = 'Only alphabets and spaces are allowed';
  } else if (formData.firstName.trim().length < 2) {
    fieldErrors.firstName = 'First name must be at least 2 characters';
  }

  // Last Name: mandatory, alphabets and spaces only, min 2 chars
  if (!formData.lastName.trim()) {
    fieldErrors.lastName = 'Last name is required';
  } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName)) {
    fieldErrors.lastName = 'Only alphabets and spaces are allowed';
  } else if (formData.lastName.trim().length < 2) {
    fieldErrors.lastName = 'Last name must be at least 2 characters';
  }

  if (!formData.email?.trim()) {
    fieldErrors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    fieldErrors.email = 'Invalid email address';
  }

  {
    const phoneErr = validatePhone(formData.phone);
    if (phoneErr) fieldErrors.phone = phoneErr;
  }

  if (!formData.company?.trim()) {
    fieldErrors.company = 'Company name is required';
  } else if (formData.company.trim().length < 3) {
    fieldErrors.company = 'Company name must be at least 3 characters';
  }

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ firstName: true, lastName: true, email: true, phone: true, company: true });
    if (!isFormValid) return;
    try {
      // Combine first + last name into `name` field for DB compatibility
      await updateCustomer.mutateAsync({
        id: customer.id,
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        email: formData.email || '',
        phone: formData.phone || '',
        company: formData.company.trim() || '',
      });
      toast.success('Customer updated successfully');
      onClose();
    } catch {
      toast.error('Failed to update customer');
    }
  };

  return (
    <ModalOverlay title="Edit Customer" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.firstName}
              onChange={(e) => update('firstName', e.target.value)}
              onBlur={() => markTouched('firstName')}
              placeholder="John"
              className={inputClass('firstName', fieldErrors)}
            />
            {touched.firstName && fieldErrors.firstName && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.firstName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.lastName}
              onChange={(e) => update('lastName', e.target.value)}
              onBlur={() => markTouched('lastName')}
              placeholder="Smith"
              className={inputClass('lastName', fieldErrors)}
            />
            {touched.lastName && fieldErrors.lastName && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.lastName}</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => update('email', e.target.value)}
              onBlur={() => markTouched('email')}
              placeholder="Enter email address"
              className={inputClass('email', fieldErrors)}
            />
            {touched.email && fieldErrors.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium">Phone</label>
            <PhoneInput
              value={formData.phone}
              onValueChange={(digits) => update('phone', digits)}
              onBlur={() => markTouched('phone')}
              placeholder="123-456-7890"
              className={inputClass('phone', fieldErrors)}
            />
            {touched.phone && fieldErrors.phone && <p className="mt-1 text-xs text-red-500">{fieldErrors.phone}</p>}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium">
            Company <span className="text-red-500">*</span>
          </label>
          <input
            value={formData.company}
            onChange={(e) => update('company', e.target.value)}
            onBlur={() => markTouched('company')}
            placeholder="Enter company name"
            className={inputClass('company', fieldErrors)}
          />
          {touched.company && fieldErrors.company ? (
            <p className="mt-1 text-xs text-red-500">{fieldErrors.company}</p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">If not a company, enter your First Name.</p>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || updateCustomer.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateCustomer.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Received Payments Tab ──

type SortField = 'date' | 'amount' | 'customerName' | 'status' | 'paymentMethod' | 'sourceType';
type SortDirection = 'asc' | 'desc';

// Human-readable label for a transaction's source type (matches the Source badge text)
function sourceTypeLabel(sourceType: string): string {
  switch (sourceType) {
    case 'pay-by-link':
      return 'Pay By Link';
    case 'pay-by-link-recurring':
      return 'Pay By Link-Recurring';
    case 'virtual-recurring':
      return 'Virtual-Recurring';
    case 'virtual-scheduled':
      return 'Virtual-Scheduled';
    default:
      return 'Virtual';
  }
}

function SortableHeader({
  label,
  field,
  sortField,
  sortDirection,
  onSort,
  align = 'left',
}: {
  label: string;
  field: SortField;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right';
}) {
  const isActive = sortField === field;
  return (
    <th
      className={`px-3 sm:px-4 py-3 text-xs font-medium text-muted-foreground whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors ${align === 'right' ? 'text-right' : 'text-left'}`}
      onClick={() => onSort(field)}
    >
      <span className={`inline-flex items-center gap-1 ${align === 'right' ? 'justify-end' : ''}`}>
        {label}
        {isActive ? (
          sortDirection === 'asc' ? (
            <ArrowUp className="h-3 w-3 text-primary" />
          ) : (
            <ArrowDown className="h-3 w-3 text-primary" />
          )
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-40" />
        )}
      </span>
    </th>
  );
}

/** Rows requested from each source for the 30-day Transactions tab. */
const TRANSACTIONS_PAGE_SIZE = 1000;

function ReceivedPaymentsTab() {
  const [statusFilter, setStatusFilter] = useState('');
  const [methodFilter, setMethodFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const { selectedLocationId } = useLocationStore();
  // Cancel / Refund are manager-and-up; employees only see the row.
  // Refund, Cancel and Resubmit each have their own permission.
  const permissions = usePermissions();
  const canRefund = permissions.has('rp.transactions.refund');
  const canCancel = permissions.has('rp.transactions.cancel');
  const canResubmit = permissions.has('rp.transactions.resubmit');

  const toggleRow = (id: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  // External RP API uses title-case statuses (e.g. "Processing", "Pending_authorization")
  const rpStatusFilter = statusFilter ? statusFilter.charAt(0).toUpperCase() + statusFilter.slice(1) : undefined;
  // Both lists are filtered client-side to the last 30 days, so ask for a page
  // large enough that the API defaults (50 payments / 100 sales) don't silently
  // drop older rows — and with them, whole statuses — from the "All Statuses" view.
  const { data: rpData, isLoading: rpLoading } = useRPPayments({
    status: rpStatusFilter,
    locationId: selectedLocationId || undefined,
    perPage: TRANSACTIONS_PAGE_SIZE,
  });
  const { data: salesData, isLoading: salesLoading } = useSaleTransactions({
    status: statusFilter || undefined,
    perPage: TRANSACTIONS_PAGE_SIZE,
  });
  const resubmitSale = useResubmitSaleTransaction();
  const cancelSale = useCancelSaleTransaction();
  const refundSale = useRefundSaleTransaction();
  const [cancelTarget, setCancelTarget] = useState<{ id: string; referenceNumber: string; amount: string } | null>(
    null,
  );
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonError, setCancelReasonError] = useState(false);
  const [refundTarget, setRefundTarget] = useState<{
    id: string;
    referenceNumber: string;
    amount: string;
    refunded: string;
    pending: string;
    remaining: string;
    remainingCents: number;
  } | null>(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundAmountError, setRefundAmountError] = useState<string | null>(null);
  const isLoading = rpLoading || salesLoading;

  const openRefundDialog = (sale: SaleTransaction) => {
    const balance = getRefundableBalance(sale);
    setRefundTarget({
      id: sale.id,
      referenceNumber: sale.referenceNumber,
      amount: sale.amount,
      ...balance,
    });
    setRefundAmount(balance.remaining);
    setRefundAmountError(null);
  };

  const closeRefundDialog = () => {
    setRefundTarget(null);
    setRefundAmount('');
    setRefundAmountError(null);
  };

  const closeCancelDialog = () => {
    setCancelTarget(null);
    setCancelReason('');
    setCancelReasonError(false);
  };

  const handleConfirmCancel = () => {
    if (!cancelTarget) return;
    const reason = cancelReason.trim();
    if (!reason) {
      setCancelReasonError(true);
      return;
    }
    cancelSale.mutate(
      { id: cancelTarget.id, reason },
      {
        onSuccess: () => {
          toast.success('Transaction cancelled');
          closeCancelDialog();
        },
        onError: (err) => {
          toast.error(apiErrorMessage(err, 'Failed to cancel transaction'));
          closeCancelDialog();
        },
      },
    );
  };

  const handleConfirmRefund = () => {
    if (!refundTarget) return;
    const error = validateRefundAmount(refundAmount, refundTarget.remainingCents);
    if (error) {
      setRefundAmountError(error);
      return;
    }
    const amount = Number(refundAmount.trim()).toFixed(2);
    refundSale.mutate(
      { id: refundTarget.id, amount },
      {
        onSuccess: (res) => {
          const remaining = Number(res.data.remainingBalance);
          toast.success(
            remaining > 0
              ? `Refund of ${formatCurrency(amount)} submitted. ${formatCurrency(res.data.remainingBalance)} still available to refund.`
              : `Refund of ${formatCurrency(amount)} submitted. Nothing is left to refund on this sale.`,
          );
          closeRefundDialog();
        },
        onError: (err) => {
          toast.error(apiErrorMessage(err, 'Failed to refund transaction'));
          closeRefundDialog();
        },
      },
    );
  };

  const handleViewAuthDoc = (e: React.MouseEvent, saleId: string) => {
    e.stopPropagation();
    window.open(`/rp-api/v1/ach-authorizations/signed-document?saleId=${encodeURIComponent(saleId)}`, '_blank');
  };

  // Normalize and merge both data sources
  const allTransactions = useMemo(() => {
    const rpPayments = (rpData?.data ?? []).map((p) => ({
      id: p.id,
      referenceNumber: p.referenceNumber,
      customerName: p.customer?.name || 'Unknown',
      invoiceNumber: p.invoice?.invoiceNumber || null,
      amount: p.amount,
      paymentMethod: p.paymentMethod,
      status: p.status,
      statusMessage: p.statusMessage,
      date: p.receivedAt,
      hasAuthDoc: false,
      email: null as string | null,
      sourceType: null as string | null,
      source: 'invoice' as const,
      raw: p,
    }));
    const sales = (salesData?.data ?? []).map((s) => ({
      id: s.id,
      referenceNumber: s.referenceNumber,
      customerName: s.firstName && s.lastName ? `${s.firstName} ${s.lastName}` : s.cardHolderName || 'Direct Sale',
      invoiceNumber: null as string | null,
      amount: s.amount,
      paymentMethod: s.paymentMethod,
      status: s.status,
      statusMessage: s.statusMessage,
      date: s.createdAt,
      hasAuthDoc: s.hasAuthDoc ?? false,
      email: s.email,
      sourceType: s.sourceType ?? null,
      source: 'sale' as const,
      raw: s,
    }));
    return [...rpPayments, ...sales];
  }, [rpData, salesData]);

  // Distinct method / source values present in the data, for the filter dropdowns
  const methodOptions = useMemo(() => {
    const set = new Set<string>();
    allTransactions.forEach((txn) => {
      if (txn.paymentMethod) set.add(txn.paymentMethod);
    });
    return Array.from(set).sort();
  }, [allTransactions]);

  const sourceOptions = useMemo(() => {
    const set = new Set<string>();
    allTransactions.forEach((txn) => {
      if (txn.sourceType) set.add(txn.sourceType);
    });
    return Array.from(set).sort();
  }, [allTransactions]);

  // Captured once at mount; full history lives under Reports
  const [nowMs] = useState(() => Date.now());

  // Admin-and-up with Reports switched on — only offer the link to people it
  // would actually open for.
  // The note links to the Receive Payments transaction history report.
  const canOpenReports = useCanOpenReports() && permissions.has('reports.rp.view');
  const openTransactionsHistory = () => {
    window.location.hash = '/reports?tab=receive&report=transactions-history';
  };

  // Filter by status and search query
  const filteredTransactions = useMemo(() => {
    // Only show transactions from the last 30 days
    const cutoff = nowMs - 30 * 24 * 60 * 60 * 1000;
    let result = allTransactions.filter((txn) => {
      const time = new Date(txn.date).getTime();
      return Number.isNaN(time) || time >= cutoff;
    });

    // Status filter — case-insensitive client-side to handle mixed-case DB values.
    // "All Statuses" (empty) shows every status, including cancelled and failed.
    if (statusFilter) {
      result = result.filter((txn) => txn.status.toLowerCase() === statusFilter.toLowerCase());
    }

    // Method filter
    if (methodFilter) {
      result = result.filter((txn) => txn.paymentMethod === methodFilter);
    }

    // Source filter
    if (sourceFilter) {
      result = result.filter((txn) => (txn.sourceType ?? '') === sourceFilter);
    }

    // Search filter
    if (search.trim()) {
      const query = search.toLowerCase();
      result = result.filter(
        (txn) =>
          txn.customerName.toLowerCase().includes(query) ||
          txn.referenceNumber?.toLowerCase().includes(query) ||
          (txn.invoiceNumber && txn.invoiceNumber.toLowerCase().includes(query)),
      );
    }

    return result;
  }, [allTransactions, search, statusFilter, methodFilter, sourceFilter, nowMs]);

  // Sort filtered results
  const sortedTransactions = useMemo(() => {
    const sorted = [...filteredTransactions];
    sorted.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'date':
          comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
          break;
        case 'amount':
          comparison = parseFloat(String(a.amount)) - parseFloat(String(b.amount));
          break;
        case 'customerName':
          comparison = a.customerName.localeCompare(b.customerName);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
        case 'paymentMethod':
          comparison = (a.paymentMethod || '').localeCompare(b.paymentMethod || '');
          break;
        case 'sourceType':
          comparison = (a.sourceType || '').localeCompare(b.sourceType || '');
          break;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [filteredTransactions, sortField, sortDirection]);

  const total = sortedTransactions.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const pageRows = useMemo(
    () => sortedTransactions.slice((page - 1) * perPage, page * perPage),
    [sortedTransactions, page, perPage],
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <div className="min-w-0 space-y-4">
      {canOpenReports ? (
        <button
          type="button"
          onClick={openTransactionsHistory}
          className="group flex w-full items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-left text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted/70"
        >
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p className="flex-1">
            Showing transactions from the last 30 days. For older transactions and full history, see the{' '}
            <span className="font-medium text-primary group-hover:underline">Reports</span> section.
          </p>
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary" />
        </button>
      ) : (
        <div className="flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
          <p>
            Showing transactions from the last 30 days. For older transactions and full history, see the{' '}
            <span className="font-medium text-foreground">Reports</span> section.
          </p>
        </div>
      )}
      <div className="flex items-center gap-3">
        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="pending_authorization">Pending Authorization</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
          <option value="returned">Returned</option>
          <option value="cancelled">Cancelled</option>
          <option value="partial refund processing">Partial Refund Processing</option>
          <option value="refund processing">Refund Processing</option>
          <option value="partially refunded">Partially Refunded</option>
          <option value="refunded">Refunded</option>
        </select>
        <select
          value={methodFilter}
          onChange={(e) => {
            setMethodFilter(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
        >
          <option value="">All Methods</option>
          {methodOptions.map((method) => (
            <option key={method} value={method}>
              {method.toUpperCase()}
            </option>
          ))}
        </select>
        <select
          value={sourceFilter}
          onChange={(e) => {
            setSourceFilter(e.target.value);
            setPage(1);
          }}
          className="h-9 rounded-lg border border-input bg-background px-3 text-sm"
        >
          <option value="">All Sources</option>
          {sourceOptions.map((source) => (
            <option key={source} value={source}>
              {sourceTypeLabel(source)}
            </option>
          ))}
        </select>
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search by customer, reference, or invoice..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="h-9 w-full rounded-lg border border-input bg-background pl-9 pr-4 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">Loading payments...</div>
      ) : sortedTransactions.length === 0 ? (
        <div className="flex h-64 items-center justify-center rounded-lg border-2 border-dashed border-border">
          {search || statusFilter || methodFilter || sourceFilter ? (
            <EmptyState
              icon={SearchX}
              title="No matching transactions"
              description={
                search || methodFilter || sourceFilter ? 'Try a different search term or clear the filters.' : undefined
              }
            />
          ) : (
            <EmptyState
              icon={ArrowDownToLine}
              title="No received payments yet"
              description="Payments collected against your invoices will appear here."
            />
          )}
        </div>
      ) : (
        <div className="rounded-lg border border-border bg-white overflow-x-auto">
          <table className="min-w-[700px] w-full">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="w-10 px-3 py-3" aria-label="Expand" />
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
                  Reference
                </th>
                <SortableHeader
                  label="Customer"
                  field="customerName"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
                  Invoice
                </th>
                <SortableHeader
                  label="Amount"
                  field="amount"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <SortableHeader
                  label="Method"
                  field="paymentMethod"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Source"
                  field="sourceType"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Status"
                  field="status"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
                  Auth Doc
                </th>
                <SortableHeader
                  label="Date"
                  field="date"
                  sortField={sortField}
                  sortDirection={sortDirection}
                  onSort={handleSort}
                />
                <th className="px-3 sm:px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((txn) => (
                <Fragment key={txn.id}>
                  <tr
                    className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer"
                    onClick={() => toggleRow(txn.id)}
                  >
                    <td className="px-3 py-3">
                      <button
                        type="button"
                        aria-label={expandedRows.has(txn.id) ? 'Collapse' : 'Expand'}
                        className="inline-flex h-6 w-6 items-center justify-center rounded hover:bg-muted"
                      >
                        {expandedRows.has(txn.id) ? (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronRight className="h-4 w-4 text-muted-foreground" />
                        )}
                      </button>
                    </td>
                    <td className="px-3 sm:px-4 py-3">
                      <span className="font-mono text-xs font-medium text-primary whitespace-nowrap">
                        {txn.referenceNumber}
                      </span>
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm whitespace-nowrap">{txn.customerName}</td>
                    <td className="px-3 sm:px-4 py-3 text-sm whitespace-nowrap">{txn.invoiceNumber || '—'}</td>
                    <td className="px-3 sm:px-4 py-3 text-sm font-medium whitespace-nowrap text-right">
                      {formatCurrency(txn.amount)}
                    </td>
                    <td className="px-3 sm:px-4 py-3">
                      <span className="uppercase text-xs font-medium whitespace-nowrap">{txn.paymentMethod}</span>
                    </td>
                    <td className="px-3 sm:px-4 py-3 whitespace-nowrap">
                      {txn.sourceType ? (
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                            txn.sourceType === 'pay-by-link'
                              ? 'bg-purple-100 text-purple-700'
                              : txn.sourceType === 'pay-by-link-recurring'
                                ? 'bg-violet-100 text-violet-700'
                                : txn.sourceType === 'virtual-recurring'
                                  ? 'bg-teal-100 text-teal-700'
                                  : txn.sourceType === 'virtual-scheduled'
                                    ? 'bg-orange-100 text-orange-700'
                                    : 'bg-blue-100 text-blue-700'
                          }`}
                        >
                          {sourceTypeLabel(txn.sourceType)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 sm:px-4 py-3">
                      <StatusBadge status={txn.status} labelMap={{ processing: 'Processed' }} />
                    </td>
                    <td className="px-3 sm:px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {txn.hasAuthDoc ? (
                        <button
                          onClick={(e) => handleViewAuthDoc(e, txn.id)}
                          className="inline-flex items-center gap-1 rounded px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
                        >
                          <FileText className="h-3.5 w-3.5" />
                          View
                        </button>
                      ) : (
                        <span className="text-muted-foreground text-sm">—</span>
                      )}
                    </td>
                    <td className="px-3 sm:px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">
                      {formatDateTime(txn.date)}
                    </td>
                    <td className="px-3 sm:px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      {canResubmit &&
                        txn.status.toLowerCase() === 'returned' &&
                        (txn.statusMessage?.includes('R01') || txn.statusMessage?.includes('R09')) && (
                          <button
                            disabled={resubmitSale.isPending}
                            onClick={() => {
                              resubmitSale.mutate(txn.id, {
                                onSuccess: () => toast.success('Transaction resubmitted successfully'),
                                onError: () => toast.error('Failed to resubmit transaction'),
                              });
                            }}
                            className="rounded-md bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive hover:bg-destructive/20 transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {resubmitSale.isPending ? 'Resubmitting...' : 'Resubmit'}
                          </button>
                        )}
                      {canCancel && txn.source === 'sale' && getSaleReversalActions(txn.raw).canCancel && (
                        <button
                          onClick={() =>
                            setCancelTarget({ id: txn.id, referenceNumber: txn.referenceNumber, amount: txn.amount })
                          }
                          className="rounded-md bg-destructive/10 px-3 py-1 text-xs font-medium text-destructive hover:bg-destructive/20 transition-colors whitespace-nowrap"
                        >
                          Cancel
                        </button>
                      )}
                      {canRefund && txn.source === 'sale' && getSaleReversalActions(txn.raw).canRefund && (
                        <button
                          onClick={() => openRefundDialog(txn.raw)}
                          className="rounded-md bg-primary/10 px-3 py-1 text-xs font-medium text-primary hover:bg-primary/20 transition-colors whitespace-nowrap"
                        >
                          Refund
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedRows.has(txn.id) && (
                    <tr className="border-b border-border">
                      <td colSpan={10} className="bg-muted/20 px-6 py-4">
                        <ExpandedTransactionDetail txn={txn} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!isLoading && total > 0 && (
        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          perPage={perPage}
          onPageChange={setPage}
          perPageOptions={PER_PAGE_OPTIONS}
          onPerPageChange={(n) => {
            setPerPage(n);
            setPage(1);
          }}
        />
      )}

      <ConfirmDialog
        open={!!cancelTarget}
        title="Cancel transaction"
        message={
          cancelTarget
            ? `Cancel ${formatCurrency(cancelTarget.amount)} ACH transaction ${cancelTarget.referenceNumber}? It will be stopped before it settles.`
            : ''
        }
        confirmLabel="Cancel transaction"
        cancelLabel="Keep"
        variant="danger"
        loading={cancelSale.isPending}
        onConfirm={handleConfirmCancel}
        onCancel={closeCancelDialog}
      >
        <label className="block text-sm font-medium text-foreground" htmlFor="cancel-reason">
          Reason (required)
        </label>
        <textarea
          id="cancel-reason"
          value={cancelReason}
          onChange={(e) => {
            setCancelReason(e.target.value);
            if (cancelReasonError) setCancelReasonError(false);
          }}
          rows={3}
          className="mt-1 w-full rounded-lg border border-border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="e.g. Customer requested cancellation"
        />
        {cancelReasonError && <p className="mt-1 text-xs text-destructive">Please enter a reason.</p>}
      </ConfirmDialog>

      <ConfirmDialog
        open={!!refundTarget}
        title="Refund transaction"
        message={
          refundTarget
            ? `Send a credit back to the customer's bank account for ACH transaction ${refundTarget.referenceNumber}. Refunds can't be undone.`
            : ''
        }
        confirmLabel="Refund"
        variant="warning"
        loading={refundSale.isPending}
        onConfirm={handleConfirmRefund}
        onCancel={closeRefundDialog}
      >
        {refundTarget && (
          <>
            <dl className="mb-3 grid grid-cols-2 gap-x-3 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Original amount</dt>
              <dd className="text-right">{formatCurrency(refundTarget.amount)}</dd>
              <dt className="text-muted-foreground">Refunded</dt>
              <dd className="text-right">{formatCurrency(refundTarget.refunded)}</dd>
              {Number(refundTarget.pending) > 0 && (
                <>
                  <dt className="text-muted-foreground">Refund in process</dt>
                  <dd className="text-right">{formatCurrency(refundTarget.pending)}</dd>
                </>
              )}
              <dt className="font-medium">Available to refund</dt>
              <dd className="text-right font-medium">{formatCurrency(refundTarget.remaining)}</dd>
            </dl>
            <label className="block text-sm font-medium text-foreground" htmlFor="refund-amount">
              Refund amount
            </label>
            <div className="relative mt-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                $
              </span>
              <input
                id="refund-amount"
                type="text"
                inputMode="decimal"
                value={refundAmount}
                onChange={(e) => {
                  setRefundAmount(e.target.value);
                  if (refundAmountError) setRefundAmountError(null);
                }}
                className="w-full rounded-lg border border-border py-2 pl-7 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
            {refundAmountError ? (
              <p className="mt-1 text-xs text-destructive">{refundAmountError}</p>
            ) : (
              <p className="mt-1 text-xs text-muted-foreground">
                Enter less than the available amount for a partial refund. You can refund the rest later.
              </p>
            )}
          </>
        )}
      </ConfirmDialog>
    </div>
  );
}

// ── Expanded Transaction Detail ──

function DetailField({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <div className="text-muted-foreground">{label}:</div>
      <div className="break-words">{value}</div>
    </>
  );
}

/** Refund totals for an ACH sale, and each refund with its own status. */
function SaleRefundDetails({ sale }: { sale: SaleTransaction }) {
  const { data } = useSaleRefunds(sale.id, true);
  const balance = getRefundableBalance(sale);
  const refunds = data?.data ?? [];

  return (
    <div>
      <h4 className="text-sm font-semibold mb-2">Refunds</h4>
      <div className="grid grid-cols-2 gap-2 text-sm max-w-2xl">
        <DetailField label="Original amount" value={formatCurrency(sale.amount)} />
        <DetailField label="Refunded" value={formatCurrency(balance.refunded)} />
        {Number(balance.pending) > 0 && (
          <DetailField label="Refund in process" value={formatCurrency(balance.pending)} />
        )}
        <DetailField
          label="Available to refund"
          value={<span className="font-medium">{formatCurrency(balance.remaining)}</span>}
        />
      </div>
      {refunds.length > 0 && (
        <div className="mt-3 max-w-2xl overflow-x-auto rounded-lg border border-border bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs text-muted-foreground">
                <th className="px-3 py-2 font-medium">Date</th>
                <th className="px-3 py-2 text-right font-medium">Amount</th>
                <th className="px-3 py-2 font-medium">Status</th>
                <th className="px-3 py-2 font-medium">Note</th>
              </tr>
            </thead>
            <tbody>
              {refunds.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0">
                  <td className="whitespace-nowrap px-3 py-2">{formatDateTime(r.createdAt)}</td>
                  <td className="px-3 py-2 text-right font-medium">{formatCurrency(r.amount)}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={r.status} labelMap={{ processing: 'In process', completed: 'Settled' }} />
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{r.statusMessage || r.initiatedByEmail || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export type ExpandedTransactionDetailTxn =
  | { source: 'invoice'; raw: RPReceivedPayment }
  | { source: 'sale'; raw: SaleTransaction };

export function ExpandedTransactionDetail({ txn }: { txn: ExpandedTransactionDetailTxn }) {
  if (txn.source === 'invoice') {
    const p = txn.raw;
    return (
      <div className="space-y-3">
        <h4 className="text-sm font-semibold">Payment Details</h4>
        <div className="grid grid-cols-2 gap-2 text-sm max-w-2xl">
          <DetailField label="Reference" value={<span className="font-mono">{p.referenceNumber}</span>} />
          <DetailField label="Customer" value={p.customer?.name || '—'} />
          {p.customer?.company && <DetailField label="Company" value={p.customer.company} />}
          <DetailField label="Invoice #" value={p.invoice?.invoiceNumber || '—'} />
          {p.invoice?.amount && <DetailField label="Invoice Amount" value={formatCurrency(p.invoice.amount)} />}
          <DetailField
            label="Amount"
            value={
              <span className="font-medium">
                {formatCurrency(p.amount)} {p.currency}
              </span>
            }
          />
          <DetailField label="Method" value={<span className="uppercase">{p.paymentMethod}</span>} />
          <DetailField
            label="Status"
            value={<StatusBadge status={p.status} labelMap={{ processing: 'Processed' }} />}
          />
          {p.statusMessage && (
            <DetailField label="Status Note" value={<span className="text-red-600">{p.statusMessage}</span>} />
          )}
          <DetailField label="Received" value={new Date(p.receivedAt).toLocaleString()} />
          <DetailField label="Created" value={new Date(p.createdAt).toLocaleString()} />
        </div>
      </div>
    );
  }

  const s = txn.raw;
  const isAch = s.paymentMethod === 'ach';
  const isCard = s.paymentMethod === 'card';
  const fullName = [s.firstName, s.lastName].filter(Boolean).join(' ') || s.cardHolderName || '—';
  const fullAddress = [s.streetAddress, s.city, s.state, s.zipCode].filter(Boolean).join(', ');
  const cardAddress = [s.cardStreetAddress, s.cardZipCode].filter(Boolean).join(', ');

  return (
    <div className="space-y-4">
      <div>
        <h4 className="text-sm font-semibold mb-2">Sale Details</h4>
        <div className="grid grid-cols-2 gap-2 text-sm max-w-2xl">
          <DetailField label="Reference" value={<span className="font-mono">{s.referenceNumber}</span>} />
          <DetailField label="Type" value={s.transactionType || '—'} />
          <DetailField
            label="Amount"
            value={
              <span className="font-medium">
                {formatCurrency(s.amount)} {s.currency}
              </span>
            }
          />
          <DetailField label="Method" value={<span className="uppercase">{s.paymentMethod}</span>} />
          <DetailField
            label="Status"
            value={<StatusBadge status={s.status} labelMap={{ processing: 'Processed' }} />}
          />
          {s.statusMessage && (
            <DetailField label="Status Note" value={<span className="text-red-600">{s.statusMessage}</span>} />
          )}
          <DetailField label="Statement Descriptor" value={s.statementDescriptor || '—'} />
          <DetailField label="Memo" value={s.memo || '—'} />
          {s.receiptDelivery && <DetailField label="Receipt" value={s.receiptDelivery} />}
          <DetailField label="Created" value={new Date(s.createdAt).toLocaleString()} />
        </div>
      </div>

      {isAch && hasRefundActivity(s) && <SaleRefundDetails sale={s} />}

      <div>
        <h4 className="text-sm font-semibold mb-2">Customer</h4>
        <div className="grid grid-cols-2 gap-2 text-sm max-w-2xl">
          <DetailField label="Name" value={fullName} />
          {s.email && <DetailField label="Email" value={s.email} />}
          {s.phone && <DetailField label="Phone" value={s.phone} />}
          {fullAddress && <DetailField label="Address" value={fullAddress} />}
          {s.idType && <DetailField label="ID Type" value={s.idType} />}
          {s.dlState && s.dlNumber && <DetailField label="DL" value={`${s.dlState} ${s.dlNumber}`} />}
        </div>
      </div>

      {isAch && (s.routingNumber || s.accountNumber || s.accountType) && (
        <div>
          <h4 className="text-sm font-semibold mb-2">Bank Account</h4>
          <div className="grid grid-cols-2 gap-2 text-sm max-w-2xl">
            {s.accountCategory && <DetailField label="Category" value={s.accountCategory} />}
            {s.accountType && <DetailField label="Type" value={s.accountType} />}
            {s.routingNumber && (
              <DetailField label="Routing #" value={<span className="font-mono">{s.routingNumber}</span>} />
            )}
            {s.accountNumber && (
              <DetailField
                label="Account #"
                value={<span className="font-mono">****{s.accountNumber.slice(-4)}</span>}
              />
            )}
            {s.authorizationType && <DetailField label="Authorization" value={s.authorizationType} />}
          </div>
        </div>
      )}

      {isCard && (s.cardLastFour || s.cardHolderName) && (
        <div>
          <h4 className="text-sm font-semibold mb-2">Card</h4>
          <div className="grid grid-cols-2 gap-2 text-sm max-w-2xl">
            {s.cardHolderName && <DetailField label="Holder" value={s.cardHolderName} />}
            {s.cardLastFour && (
              <DetailField label="Card #" value={<span className="font-mono">****{s.cardLastFour}</span>} />
            )}
            {s.cardExpirationDate && <DetailField label="Expires" value={s.cardExpirationDate} />}
            {cardAddress && <DetailField label="Billing Address" value={cardAddress} />}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Modal Overlay ──

function ModalOverlay({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 pt-[10vh]">
      <div className={`relative w-full ${wide ? 'max-w-2xl' : 'max-w-lg'} rounded-xl bg-white shadow-xl`}>
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-lg font-semibold text-foreground">{title}</h2>
          <button onClick={onClose} className="rounded-md p-1 hover:bg-muted">
            <X className="h-5 w-5 text-muted-foreground" />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

// ── Edit Invoice Modal ──

function EditInvoiceModal({ invoice, onClose }: { invoice: RPInvoice; onClose: () => void }) {
  const updateInvoice = useUpdateRPInvoice();
  const { data: invoiceData, isLoading: invoiceLoading } = useRPInvoice(invoice.id);
  const { data: customerData } = useRPCustomers();
  const customers = (customerData?.data ?? []).filter((c: RPCustomer) => c.isActive);

  const fullInvoice = invoiceData?.data;

  const [customerId, setCustomerId] = useState(invoice.customerId || '');
  const [dueDate, setDueDate] = useState(invoice.dueDate ? new Date(invoice.dueDate).toISOString().split('T')[0] : '');
  const [memo, setMemo] = useState(invoice.memo || '');
  const [lines, setLines] = useState<{ description: string; quantity: number; unitPrice: number }[]>([]);
  const [linesInitialized, setLinesInitialized] = useState(false);

  // Initialize lines from fetched invoice data
  if (fullInvoice?.lines && fullInvoice.lines.length > 0 && !linesInitialized) {
    setLines(
      fullInvoice.lines.map((l: RPInvoiceLine) => ({
        description: l.description,
        quantity: l.quantity,
        unitPrice: parseFloat(l.unitPrice),
      })),
    );
    setLinesInitialized(true);
  }

  const addLine = () => setLines((prev) => [...prev, { description: '', quantity: 1, unitPrice: 0 }]);
  const removeLine = (index: number) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
  };
  const updateLine = (index: number, field: string, value: string | number) => {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, [field]: value } : line)));
  };

  const total = lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);
  const isValid =
    customerId && lines.length > 0 && lines.every((l) => l.description && l.quantity > 0 && l.unitPrice > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await updateInvoice.mutateAsync({
        id: invoice.id,
        customerId,
        dueDate: dueDate || undefined,
        memo: memo || undefined,
        lines,
      });
      toast.success('Invoice updated successfully');
      onClose();
    } catch (err: any) {
      const msg = err?.error?.message || err?.message || 'Failed to update invoice';
      toast.error(msg);
    }
  };

  if (invoiceLoading) {
    return (
      <ModalOverlay title="Edit Invoice" onClose={onClose} wide>
        <div className="flex h-32 items-center justify-center text-muted-foreground">Loading invoice...</div>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay title="Edit Invoice" onClose={onClose} wide>
      <p className="mb-4 text-sm text-muted-foreground">
        Editing <span className="font-medium text-foreground">{invoice.invoiceNumber}</span>
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground">
              Customer <span className="text-red-500">*</span>
            </label>
            <select
              required
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm"
            >
              <option value="">Select customer</option>
              {customers.map((c: RPCustomer) => (
                <option key={c.id} value={c.id}>
                  {c.name} {c.company ? `(${c.company})` : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground">Due Date</label>
            <DateInput
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground">
            Memo <span className="text-xs text-muted-foreground">(optional)</span>
          </label>
          <input
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="Invoice description..."
            className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Line Items */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-foreground">Line Items</h4>
            <button
              type="button"
              onClick={addLine}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              <Plus className="h-3 w-3" />
              Add Line
            </button>
          </div>

          <div className="space-y-3">
            {lines.map((line, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  {index === 0 && (
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
                  )}
                  <input
                    required
                    value={line.description}
                    onChange={(e) => updateLine(index, 'description', e.target.value)}
                    placeholder="Item description"
                    className="h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div className="col-span-2">
                  {index === 0 && <label className="block text-xs font-medium text-muted-foreground mb-1">Qty</label>}
                  <input
                    type="number"
                    required
                    min="1"
                    value={line.quantity}
                    onChange={(e) => updateLine(index, 'quantity', parseInt(e.target.value) || 0)}
                    className="h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div className="col-span-3">
                  {index === 0 && (
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Unit Price</label>
                  )}
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                    <input
                      type="number"
                      required
                      step="0.01"
                      min="0.01"
                      value={line.unitPrice || ''}
                      onChange={(e) => updateLine(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="h-9 w-full rounded-lg border border-input pl-6 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <span className="text-sm font-medium">{formatCurrency(line.quantity * line.unitPrice)}</span>
                  {lines.length > 1 && (
                    <button type="button" onClick={() => removeLine(index)} className="rounded p-1 hover:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 flex justify-end border-t border-border pt-3">
            <div className="text-sm">
              <span className="text-muted-foreground">Total: </span>
              <span className="text-lg font-bold text-primary">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || updateInvoice.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateInvoice.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── New Invoice Modal ──

function NewInvoiceModal({ onClose }: { onClose: () => void }) {
  const createInvoice = useCreateRPInvoice();
  const { data: customerData } = useRPCustomers();
  const customers = (customerData?.data ?? []).filter((c: RPCustomer) => c.isActive);
  const { selectedLocationId } = useLocationStore();

  const [customerId, setCustomerId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [memo, setMemo] = useState('');
  const [lines, setLines] = useState<{ description: string; quantity: number; unitPrice: number }[]>([
    { description: '', quantity: 1, unitPrice: 0 },
  ]);
  const [showAddCustomer, setShowAddCustomer] = useState(false);

  const addLine = () => setLines((prev) => [...prev, { description: '', quantity: 1, unitPrice: 0 }]);
  const removeLine = (index: number) => {
    if (lines.length <= 1) return;
    setLines((prev) => prev.filter((_, i) => i !== index));
  };
  const updateLine = (index: number, field: string, value: string | number) => {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, [field]: value } : line)));
  };

  const total = lines.reduce((sum, line) => sum + line.quantity * line.unitPrice, 0);
  const isValid =
    customerId && selectedLocationId && lines.every((l) => l.description && l.quantity > 0 && l.unitPrice > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedLocationId) {
      toast.error('Select a location before creating an invoice');
      return;
    }
    try {
      await createInvoice.mutateAsync({
        customerId,
        locationId: selectedLocationId,
        dueDate: dueDate || undefined,
        memo: memo || undefined,
        lines,
      });
      toast.success('Invoice created successfully');
      onClose();
    } catch (err: any) {
      const msg = err?.error?.message || err?.message || 'Failed to create invoice';
      toast.error(msg);
    }
  };

  return (
    <ModalOverlay title="Create Invoice" onClose={onClose} wide>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground">
              Customer <span className="text-red-500">*</span>
            </label>
            <div className="mt-1 flex gap-1">
              <SearchableSelect
                className="flex-1 min-w-0"
                value={customerId}
                onChange={setCustomerId}
                options={customers.map((c: RPCustomer) => ({
                  value: c.id,
                  label: c.company ? `${c.name} (${c.company})` : c.name,
                }))}
                placeholder="Select customer"
                searchPlaceholder="Search customers..."
                emptyMessage="No customers found"
              />
              <button
                type="button"
                onClick={() => setShowAddCustomer(true)}
                className="shrink-0 inline-flex items-center gap-1 rounded-lg border border-dashed border-primary/50 px-2 text-xs font-medium text-primary hover:bg-primary/5"
                title="Add new customer"
              >
                <Plus className="h-3 w-3" /> New
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground">Due Date</label>
            <DateInput
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground">
            Memo <span className="text-xs text-muted-foreground">(optional)</span>
          </label>
          <input
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            placeholder="Invoice description..."
            className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        {/* Line Items */}
        <div className="border-t border-border pt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-foreground">Line Items</h4>
            <button
              type="button"
              onClick={addLine}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
            >
              <Plus className="h-3 w-3" />
              Add Line
            </button>
          </div>

          <div className="space-y-3">
            {lines.map((line, index) => (
              <div key={index} className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-5">
                  {index === 0 && (
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
                  )}
                  <input
                    required
                    value={line.description}
                    onChange={(e) => updateLine(index, 'description', e.target.value)}
                    placeholder="Item description"
                    className="h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div className="col-span-2">
                  {index === 0 && <label className="block text-xs font-medium text-muted-foreground mb-1">Qty</label>}
                  <input
                    type="number"
                    required
                    min="1"
                    value={line.quantity}
                    onChange={(e) => updateLine(index, 'quantity', parseInt(e.target.value) || 0)}
                    className="h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div className="col-span-3">
                  {index === 0 && (
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Unit Price</label>
                  )}
                  <div className="relative">
                    <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
                    <input
                      type="number"
                      required
                      step="0.01"
                      min="0.01"
                      value={line.unitPrice || ''}
                      onChange={(e) => updateLine(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                      placeholder="0.00"
                      className="h-9 w-full rounded-lg border border-input pl-6 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                    />
                  </div>
                </div>
                <div className="col-span-2 flex items-center gap-2">
                  <span className="text-sm font-medium">{formatCurrency(line.quantity * line.unitPrice)}</span>
                  {lines.length > 1 && (
                    <button type="button" onClick={() => removeLine(index)} className="rounded p-1 hover:bg-red-50">
                      <Trash2 className="h-3.5 w-3.5 text-red-400" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-3 flex justify-end border-t border-border pt-3">
            <div className="text-sm">
              <span className="text-muted-foreground">Total: </span>
              <span className="text-lg font-bold text-primary">{formatCurrency(total)}</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || createInvoice.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {createInvoice.isPending ? 'Creating...' : 'Create Invoice'}
          </button>
        </div>
      </form>
      {showAddCustomer && (
        <NewCustomerModal onClose={() => setShowAddCustomer(false)} onCreated={(c) => setCustomerId(c.id)} />
      )}
    </ModalOverlay>
  );
}

// ── New Customer Modal ──

function NewCustomerModal({ onClose, onCreated }: { onClose: () => void; onCreated?: (customer: RPCustomer) => void }) {
  const createCustomer = useCreateRPCustomer();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    company: '',
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const update = (field: string, value: string) => {
    // Name fields: allow only alphabets and spaces
    if (field === 'firstName' || field === 'lastName') {
      value = value.replace(/[^a-zA-Z\s]/g, '');
    }
    // Phone: allow only numbers
    if (field === 'phone') {
      value = sanitizePhone(value);
    }
    // Company: allow only alphabets and spaces
    if (field === 'company') {
      value = value.replace(/[^a-zA-Z\s]/g, '');
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const inputClass = (field: string, errors: Record<string, string>) =>
    `mt-1 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${
      touched[field] && errors[field]
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-input focus:border-primary focus:ring-primary'
    }`;

  // Validation logic
  const fieldErrors: Record<string, string> = {};

  // First Name: mandatory, alphabets and spaces only, min 2 chars
  if (!formData.firstName.trim()) {
    fieldErrors.firstName = 'First name is required';
  } else if (!/^[a-zA-Z\s]+$/.test(formData.firstName)) {
    fieldErrors.firstName = 'Only alphabets and spaces are allowed';
  } else if (formData.firstName.trim().length < 2) {
    fieldErrors.firstName = 'First name must be at least 2 characters';
  }

  // Last Name: mandatory, alphabets and spaces only, min 2 chars
  if (!formData.lastName.trim()) {
    fieldErrors.lastName = 'Last name is required';
  } else if (!/^[a-zA-Z\s]+$/.test(formData.lastName)) {
    fieldErrors.lastName = 'Only alphabets and spaces are allowed';
  } else if (formData.lastName.trim().length < 2) {
    fieldErrors.lastName = 'Last name must be at least 2 characters';
  }

  // Email: required
  if (!formData.email?.trim()) {
    fieldErrors.email = 'Email is required';
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
    fieldErrors.email = 'Invalid email address';
  }

  // Phone: optional, must be exactly 10 digits if provided
  {
    const phoneErr = validatePhone(formData.phone);
    if (phoneErr) fieldErrors.phone = phoneErr;
  }

  // Company: required, min 3 chars
  if (!formData.company?.trim()) {
    fieldErrors.company = 'Company name is required';
  } else if (formData.company.trim().length < 3) {
    fieldErrors.company = 'Company name must be at least 3 characters';
  }

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ firstName: true, lastName: true, email: true, phone: true, company: true });
    if (!isFormValid) return;
    try {
      // Combine first + last name into `name` field for DB compatibility
      const result = await createCustomer.mutateAsync({
        name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
        email: formData.email || '',
        phone: formData.phone || '',
        company: formData.company.trim() || '',
      });
      toast.success('Customer created successfully');
      if (onCreated && result?.data) onCreated(result.data);
      onClose();
    } catch (err: any) {
      // Surface the actual backend reason (validation, duplicate, etc.) instead
      // of the previous generic toast that hid every failure behind one message.
      const msg = err?.error?.message || err?.message || 'Failed to create customer';
      toast.error(msg);
    }
  };

  return (
    <ModalOverlay title="Add Customer" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.firstName}
              onChange={(e) => update('firstName', e.target.value)}
              onBlur={() => markTouched('firstName')}
              placeholder="John"
              className={inputClass('firstName', fieldErrors)}
            />
            {touched.firstName && fieldErrors.firstName && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.firstName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.lastName}
              onChange={(e) => update('lastName', e.target.value)}
              onBlur={() => markTouched('lastName')}
              placeholder="Smith"
              className={inputClass('lastName', fieldErrors)}
            />
            {touched.lastName && fieldErrors.lastName && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.lastName}</p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => update('email', e.target.value)}
              onBlur={() => markTouched('email')}
              placeholder="Enter email address"
              className={inputClass('email', fieldErrors)}
            />
            {touched.email && fieldErrors.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium">Phone</label>
            <PhoneInput
              value={formData.phone}
              onValueChange={(digits) => update('phone', digits)}
              onBlur={() => markTouched('phone')}
              placeholder="123-456-7890"
              className={inputClass('phone', fieldErrors)}
            />
            {touched.phone && fieldErrors.phone && <p className="mt-1 text-xs text-red-500">{fieldErrors.phone}</p>}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium">
            Company <span className="text-red-500">*</span>
          </label>
          <input
            value={formData.company}
            onChange={(e) => update('company', e.target.value)}
            onBlur={() => markTouched('company')}
            placeholder="Enter company name"
            className={inputClass('company', fieldErrors)}
          />
          {touched.company && fieldErrors.company ? (
            <p className="mt-1 text-xs text-red-500">{fieldErrors.company}</p>
          ) : (
            <p className="mt-1 text-xs text-muted-foreground">If not a company, enter your First Name.</p>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || createCustomer.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {createCustomer.isPending ? 'Creating...' : 'Create Customer'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Record Payment Modal ──

function RecordPaymentModal({ onClose }: { onClose: () => void }) {
  const recordPayment = useRecordRPPayment();
  const { data: invoiceData } = useRPInvoices({ status: 'pending' });
  const invoices = invoiceData?.data ?? [];

  // Also load sent/partial invoices
  const { data: sentData } = useRPInvoices({ status: 'sent' });
  const { data: partialData } = useRPInvoices({ status: 'partial' });
  const allPayableInvoices = [...invoices, ...(sentData?.data ?? []), ...(partialData?.data ?? [])];

  const [invoiceId, setInvoiceId] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('ach_others');
  const [memo, setMemo] = useState('');

  const selectedInvoice = useMemo(
    () => allPayableInvoices.find((inv) => inv.id === invoiceId),
    [allPayableInvoices, invoiceId],
  );

  const isValid = invoiceId && amount && parseFloat(amount) > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await recordPayment.mutateAsync({
        invoiceId,
        amount: parseFloat(amount),
        paymentMethod,
        memo: memo || undefined,
      });
      toast.success('Payment recorded successfully');
      onClose();
    } catch {
      toast.error('Failed to record payment');
    }
  };

  return (
    <ModalOverlay title="Record Payment" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground">
            Invoice <span className="text-red-500">*</span>
          </label>
          <select
            required
            value={invoiceId}
            onChange={(e) => {
              setInvoiceId(e.target.value);
              const inv = allPayableInvoices.find((i) => i.id === e.target.value);
              if (inv) setAmount(inv.amount);
            }}
            className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm"
          >
            <option value="">Select invoice</option>
            {allPayableInvoices.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {inv.invoiceNumber} — {inv.customer?.name} — {formatCurrency(inv.amount)}
              </option>
            ))}
          </select>
          {selectedInvoice && (
            <div className="mt-1.5 flex items-center gap-2 rounded-md bg-muted/50 px-2.5 py-1.5 text-xs text-muted-foreground">
              <Receipt className="h-3 w-3" />
              {selectedInvoice.customer?.name} — Due: {formatCurrency(selectedInvoice.amount)}
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground">
              Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative mt-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <input
                type="number"
                required
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="h-9 w-full rounded-lg border border-input pl-7 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground">Payment Method</label>
            <select
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm"
            >
              <option value="ach_others">ACH - Others</option>
              <option value="wire">Wire Transfer</option>
              <option value="check">Check</option>
              <option value="card_others">Card - Others</option>
              <option value="cash">Cash</option>
              <option value="others">Others</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground">
            Memo <span className="text-xs text-muted-foreground">(optional)</span>
          </label>
          <textarea
            value={memo}
            onChange={(e) => setMemo(e.target.value)}
            rows={2}
            placeholder="Payment notes..."
            className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || recordPayment.isPending}
            className="inline-flex items-center gap-2 h-9 rounded-lg bg-green-600 px-4 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
          >
            <CheckCircle2 className="h-4 w-4" />
            {recordPayment.isPending ? 'Recording...' : 'Record Payment'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── New Payment Link Modal ──

function NewPaymentLinkModal({ onClose }: { onClose: () => void }) {
  const createPaymentLink = useCreateRPPaymentLink();
  const { selectedLocationId } = useLocationStore();
  const { data: locationsData } = useLocationsWithExternalId();
  const selectedLocation = locationsData?.data?.find((l: any) => l.id === selectedLocationId);
  const merchantId = selectedLocation?.externalId || null;
  const { data: customerData } = useRPCustomers();
  const customers = (customerData?.data ?? []).filter((c: RPCustomer) => c.isActive);

  const [customerId, setCustomerId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [amount, setAmount] = useState('');
  const [invoiceId, setInvoiceId] = useState('');
  const [notes, setNotes] = useState('');
  const [statementDescriptor, setStatementDescriptor] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [recurringEnabled, setRecurringEnabled] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState<RecurringFrequency>('monthly');
  const [recurringStartDate, setRecurringStartDate] = useState('');
  const [recurringStartTime, setRecurringStartTime] = useState('09:00');
  const [recurringHasEnd, setRecurringHasEnd] = useState(false);
  const [recurringEndDate, setRecurringEndDate] = useState('');
  const [recurringError, setRecurringError] = useState('');

  const { data: invoiceData } = useRPInvoices({
    customerId: customerId || undefined,
    locationId: selectedLocationId || undefined,
  });
  const invoices = invoiceData?.data ?? [];

  const validate = () => {
    const next: Record<string, string> = {};
    if (!customerId) next.customerId = 'Customer is required';
    if (!dueDate) next.dueDate = 'Expiry date is required';
    if (!amount) {
      next.amount = 'Amount is required';
    } else {
      const parsed = parseFloat(amount);
      if (Number.isNaN(parsed) || parsed <= 0) {
        next.amount = 'Amount must be greater than 0';
      }
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    let recurringPayload: {
      isRecurring: true;
      recurringFrequency: RecurringFrequency;
      recurringStartAt: string;
      recurringEndAt: string | null;
    } | null = null;
    if (recurringEnabled) {
      if (!recurringStartDate) {
        setRecurringError('Start date is required');
        return;
      }
      const startAtIso = new Date(`${recurringStartDate}T${recurringStartTime || '00:00'}:00`).toISOString();
      if (new Date(startAtIso) < new Date()) {
        setRecurringError('Start date cannot be in the past');
        return;
      }
      let endAtIso: string | null = null;
      if (recurringHasEnd) {
        if (!recurringEndDate) {
          setRecurringError('End date is required');
          return;
        }
        const endDate = new Date(`${recurringEndDate}T23:59:59`);
        if (endDate <= new Date(startAtIso)) {
          setRecurringError('End date must be after the start date');
          return;
        }
        endAtIso = endDate.toISOString();
      }
      setRecurringError('');
      recurringPayload = {
        isRecurring: true,
        recurringFrequency,
        recurringStartAt: startAtIso,
        recurringEndAt: endAtIso,
      };
    }

    try {
      await createPaymentLink.mutateAsync({
        customerId,
        dueDate,
        amount: parseFloat(amount),
        invoiceId: invoiceId || undefined,
        notes: notes.trim() || undefined,
        statementDescriptor: statementDescriptor.trim() || undefined,
        locationId: selectedLocationId || undefined,
        merchantId: merchantId || undefined,
        ...(recurringPayload ?? {}),
      });
      toast.success('Payment link created successfully');
      onClose();
    } catch (err) {
      const message = (err as { error?: { message?: string } })?.error?.message;
      toast.error(message || 'Failed to create payment link');
    }
  };

  return (
    <ModalOverlay title="Create Payment Link" onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground">
            Customer <span className="text-red-500">*</span>
          </label>
          <select
            value={customerId}
            onChange={(e) => {
              setCustomerId(e.target.value);
              setInvoiceId('');
              if (errors.customerId) setErrors((p) => ({ ...p, customerId: '' }));
            }}
            className={`mt-1 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${
              errors.customerId
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-input focus:border-primary focus:ring-primary'
            }`}
          >
            <option value="">Select customer</option>
            {customers.map((c: RPCustomer) => (
              <option key={c.id} value={c.id}>
                {c.name}
                {c.company ? ` (${c.company})` : ''}
              </option>
            ))}
          </select>
          {errors.customerId && <p className="mt-1 text-xs text-red-500">{errors.customerId}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground">
              Expiry Date <span className="text-red-500">*</span>
            </label>
            <DateInput
              value={dueDate}
              min={(() => {
                const d = new Date();
                return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
              })()}
              onChange={(e) => {
                setDueDate(e.target.value);
                if (errors.dueDate) setErrors((p) => ({ ...p, dueDate: '' }));
              }}
              className={`mt-1 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${
                errors.dueDate
                  ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                  : 'border-input focus:border-primary focus:ring-primary'
              }`}
            />
            {errors.dueDate && <p className="mt-1 text-xs text-red-500">{errors.dueDate}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground">
              Amount <span className="text-red-500">*</span>
            </label>
            <div className="relative mt-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <input
                type="number"
                step="0.01"
                min="0.01"
                value={amount}
                onChange={(e) => {
                  setAmount(e.target.value);
                  if (errors.amount) setErrors((p) => ({ ...p, amount: '' }));
                }}
                placeholder="0.00"
                className={`h-9 w-full rounded-lg border pl-6 pr-3 text-sm outline-none focus:ring-1 ${
                  errors.amount
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                    : 'border-input focus:border-primary focus:ring-primary'
                }`}
              />
            </div>
            {errors.amount && <p className="mt-1 text-xs text-red-500">{errors.amount}</p>}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground">
            Invoice <span className="text-xs text-muted-foreground">(optional)</span>
          </label>
          <select
            value={invoiceId}
            onChange={(e) => setInvoiceId(e.target.value)}
            disabled={!customerId}
            className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary disabled:bg-muted disabled:cursor-not-allowed"
          >
            <option value="">{customerId ? 'Select invoice' : 'Select customer first'}</option>
            {invoices.map((inv) => (
              <option key={inv.id} value={inv.id}>
                {inv.invoiceNumber} — {formatCurrency(inv.amount)}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground">
            Notes / Description <span className="text-xs text-muted-foreground">(optional)</span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="References such as Notes, Invoice No, Bill No, Purpose etc..."
            rows={3}
            className="mt-1 w-full rounded-lg border border-input px-3 py-2 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-foreground">
            Statement / Transaction Descriptor{' '}
            <span className="text-xs text-muted-foreground">(optional, max 10 chars)</span>
          </label>
          <input
            type="text"
            value={statementDescriptor}
            onChange={(e) => setStatementDescriptor(e.target.value.slice(0, 10))}
            placeholder="e.g. ACME"
            maxLength={10}
            className="mt-1 h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
          />
        </div>

        <div className="rounded-lg border border-border p-4 space-y-4">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={recurringEnabled}
              onChange={(e) => {
                const checked = e.target.checked;
                setRecurringEnabled(checked);
                setRecurringError('');
                if (checked && !recurringStartDate) {
                  const d = new Date();
                  setRecurringStartDate(
                    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`,
                  );
                }
              }}
              className="mt-0.5 h-4 w-4 accent-primary"
            />
            <span className="text-sm">
              <span className="font-medium text-foreground">Make this a recurring payment link</span>
              <span className="block text-xs text-muted-foreground">
                Schedule this payment link to repeat automatically on the cadence you choose.
              </span>
            </span>
          </label>

          {recurringEnabled && (
            <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-foreground">
                  Frequency <span className="text-red-500">*</span>
                </label>
                <select
                  value={recurringFrequency}
                  onChange={(e) => setRecurringFrequency(e.target.value as RecurringFrequency)}
                  className="h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                >
                  {RECURRING_FREQUENCIES.map((freq) => (
                    <option key={freq} value={freq}>
                      {RECURRING_FREQUENCY_LABELS[freq]}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <DateInput
                    value={recurringStartDate}
                    min={(() => {
                      const d = new Date();
                      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                    })()}
                    onChange={(e) => {
                      setRecurringStartDate(e.target.value);
                      if (recurringError) setRecurringError('');
                    }}
                    className="h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-foreground">
                    Start Time <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="time"
                    value={recurringStartTime}
                    onChange={(e) => setRecurringStartTime(e.target.value)}
                    className="h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-foreground">End</label>
                <div className="flex items-center gap-6">
                  <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                    <input
                      type="radio"
                      name="paymentLinkRecurringEnd"
                      checked={!recurringHasEnd}
                      onChange={() => setRecurringHasEnd(false)}
                      className="h-4 w-4 accent-primary"
                    />
                    <span className="text-sm">No end date</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer whitespace-nowrap">
                    <input
                      type="radio"
                      name="paymentLinkRecurringEnd"
                      checked={recurringHasEnd}
                      onChange={() => setRecurringHasEnd(true)}
                      className="h-4 w-4 accent-primary"
                    />
                    <span className="text-sm">End on</span>
                  </label>
                </div>
                {recurringHasEnd && (
                  <DateInput
                    value={recurringEndDate}
                    min={
                      recurringStartDate ||
                      (() => {
                        const d = new Date();
                        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                      })()
                    }
                    onChange={(e) => {
                      setRecurringEndDate(e.target.value);
                      if (recurringError) setRecurringError('');
                    }}
                    className="mt-2 h-9 w-full rounded-lg border border-input px-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                )}
              </div>

              {recurringError && (
                <p className="rounded border border-red-300 bg-red-50 p-2 text-xs text-red-700" role="alert">
                  {recurringError}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createPaymentLink.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {createPaymentLink.isPending ? 'Creating...' : 'Create Payment Link'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Add Card Details Modal ──

function AddCardDetailsModal({ customer, onClose }: { customer: RPCustomer; onClose: () => void }) {
  const createCard = useCreateRPCustomerCard();
  const [formData, setFormData] = useState({
    cardNumber: '',
    cardHolderName: '',
    expirationDate: '',
    cvv: '',
    cardZipCode: '',
    streetAddress: '',
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const update = (field: string, value: string) => {
    if (field === 'cardNumber') {
      const digits = value.replace(/\D/g, '').slice(0, 19);
      const formatted = digits.replace(/(.{4})/g, '$1 ').trim();
      setFormData((prev) => ({ ...prev, cardNumber: formatted }));
      return;
    }
    if (field === 'cvv') {
      value = value.replace(/\D/g, '').slice(0, 4);
    }
    if (field === 'cardZipCode') {
      value = value.replace(/\D/g, '').slice(0, 5);
    }
    if (field === 'cardHolderName') {
      value = value.replace(/[^a-zA-Z\s]/g, '');
    }
    if (field === 'expirationDate') {
      let v = value.replace(/[^0-9/]/g, '');
      const slashCount = (v.match(/\//g) || []).length;
      if (slashCount > 1) v = v.replace(/\/([^]*)$/, '$1');
      const digits = v.replace(/\//g, '');
      if (digits.length >= 2 && !v.includes('/')) {
        v = digits.slice(0, 2) + '/' + digits.slice(2);
      }
      v = v.slice(0, 5);
      setFormData((prev) => ({ ...prev, expirationDate: v }));
      return;
    }
    if (field === 'streetAddress') {
      value = value.slice(0, 127);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const fieldErrors = useMemo<Record<string, string>>(() => {
    const errors: Record<string, string> = {};
    const rawCardNumber = formData.cardNumber.replace(/\s/g, '');
    if (!rawCardNumber) {
      errors.cardNumber = 'Card number is required';
    } else if (rawCardNumber.length < 12 || rawCardNumber.length > 19) {
      errors.cardNumber = 'Card number must be between 12 and 19 digits';
    }
    if (!formData.expirationDate) {
      errors.expirationDate = 'Expiration date is required';
    } else if (!/^\d{2}\/\d{2}$/.test(formData.expirationDate)) {
      errors.expirationDate = 'Expiration date must be in MM/YY format';
    } else {
      const [mm, yy] = formData.expirationDate.split('/').map(Number);
      if (mm < 1 || mm > 12) {
        errors.expirationDate = 'Invalid month';
      } else {
        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear() % 100;
        if (yy < currentYear || (yy === currentYear && mm < currentMonth)) {
          errors.expirationDate = 'Card has expired';
        }
      }
    }
    if (!formData.cvv) {
      errors.cvv = 'CVV is required';
    } else if (formData.cvv.length < 3 || formData.cvv.length > 4) {
      errors.cvv = 'CVV must be 3 or 4 digits';
    }
    if (!formData.cardZipCode) {
      errors.cardZipCode = 'Zip code is required';
    } else if (formData.cardZipCode.length !== 5) {
      errors.cardZipCode = 'Zip code must be 5 digits';
    }
    return errors;
  }, [formData]);

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const inputClass = (field: string) =>
    `mt-1 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${
      touched[field] && fieldErrors[field]
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-input focus:border-primary focus:ring-primary'
    }`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({
      cardNumber: true,
      expirationDate: true,
      cvv: true,
      cardZipCode: true,
      cardHolderName: true,
      streetAddress: true,
    });
    if (!isFormValid) return;
    try {
      const rawCardNumber = formData.cardNumber.replace(/\s/g, '');
      await createCard.mutateAsync({
        customerId: customer.id,
        customerName: customer.name,
        cardLastFour: rawCardNumber.slice(-4),
        cardHolderName: formData.cardHolderName.trim() || null,
        expirationDate: formData.expirationDate,
        cardZipCode: formData.cardZipCode,
        streetAddress: formData.streetAddress.trim() || null,
      });
      toast.success('Card details added successfully');
      onClose();
    } catch {
      toast.error('Failed to add card details');
    }
  };

  return (
    <ModalOverlay title="Card Details" onClose={onClose}>
      <p className="mb-4 text-sm text-muted-foreground">
        Adding card for <span className="font-medium text-foreground">{customer.name}</span>
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium">
            Card Number <span className="text-red-500">*</span>
          </label>
          <input
            value={formData.cardNumber}
            onChange={(e) => update('cardNumber', e.target.value)}
            onBlur={() => markTouched('cardNumber')}
            placeholder="1234 5678 9012 3456"
            className={inputClass('cardNumber')}
          />
          {touched.cardNumber && fieldErrors.cardNumber && (
            <p className="mt-1 text-xs text-red-500">{fieldErrors.cardNumber}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium">Card Holder Name</label>
          <input
            value={formData.cardHolderName}
            onChange={(e) => update('cardHolderName', e.target.value)}
            onBlur={() => markTouched('cardHolderName')}
            placeholder="Name on card"
            className={inputClass('cardHolderName')}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">
              Expiration Date <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.expirationDate}
              onChange={(e) => update('expirationDate', e.target.value)}
              onBlur={() => markTouched('expirationDate')}
              placeholder="MM/YY"
              className={inputClass('expirationDate')}
            />
            {touched.expirationDate && fieldErrors.expirationDate && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.expirationDate}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium">
              CVV <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.cvv}
              onChange={(e) => update('cvv', e.target.value)}
              onBlur={() => markTouched('cvv')}
              placeholder="123"
              className={inputClass('cvv')}
            />
            {touched.cvv && fieldErrors.cvv && <p className="mt-1 text-xs text-red-500">{fieldErrors.cvv}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">
              Zip Code <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.cardZipCode}
              onChange={(e) => update('cardZipCode', e.target.value)}
              onBlur={() => markTouched('cardZipCode')}
              placeholder="12345"
              className={inputClass('cardZipCode')}
            />
            {touched.cardZipCode && fieldErrors.cardZipCode && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.cardZipCode}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium">Street Address</label>
            <input
              value={formData.streetAddress}
              onChange={(e) => update('streetAddress', e.target.value)}
              placeholder="123 Main St"
              className={inputClass('streetAddress')}
            />
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || createCard.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {createCard.isPending ? 'Saving...' : 'Save Card Details'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Add Bank Details Modal ──

function AddBankDetailsModal({ customer, onClose }: { customer: RPCustomer; onClose: () => void }) {
  const createBank = useCreateRPCustomerBank();
  const [formData, setFormData] = useState({
    accountCategory: 'business',
    accountType: 'checking',
    routingNumber: '',
    accountNumber: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const update = (field: string, value: string) => {
    if (field === 'firstName' || field === 'lastName') {
      value = value.replace(/[^a-zA-Z\s]/g, '');
    }
    if (field === 'routingNumber') {
      value = value.replace(/\D/g, '').slice(0, 9);
    }
    if (field === 'accountNumber') {
      value = value.replace(/\D/g, '').slice(0, 17);
    }
    if (field === 'phone') {
      value = sanitizePhone(value);
    }
    if (field === 'zipCode') {
      value = value.replace(/\D/g, '').slice(0, 5);
    }
    if (field === 'streetAddress') {
      value = value.slice(0, 127);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const fieldErrors = useMemo<Record<string, string>>(() => {
    const errors: Record<string, string> = {};
    if (!formData.routingNumber) {
      errors.routingNumber = 'Routing number is required';
    } else if (formData.routingNumber.length !== 9) {
      errors.routingNumber = 'Routing number must be 9 digits';
    }
    if (!formData.accountNumber) {
      errors.accountNumber = 'Account number is required';
    } else if (formData.accountNumber.length < 6) {
      errors.accountNumber = 'Account number must be at least 6 digits';
    } else if (formData.accountNumber.length > 17) {
      errors.accountNumber = 'Account number must not exceed 17 digits';
    }
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }
    {
      const phoneErr = validatePhone(formData.phone, true);
      if (phoneErr) errors.phone = phoneErr;
    }
    if (formData.zipCode && formData.zipCode.length !== 5) {
      errors.zipCode = 'Zip code must be 5 digits';
    }
    return errors;
  }, [formData]);

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const inputClass = (field: string) =>
    `mt-1 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${
      touched[field] && fieldErrors[field]
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-input focus:border-primary focus:ring-primary'
    }`;

  const selectClass = (field: string) =>
    `mt-1 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${
      touched[field] && fieldErrors[field]
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-input focus:border-primary focus:ring-primary'
    }`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({
      routingNumber: true,
      accountNumber: true,
      firstName: true,
      lastName: true,
      phone: true,
      email: true,
      zipCode: true,
      streetAddress: true,
      city: true,
      state: true,
    });
    if (!isFormValid) return;
    try {
      await createBank.mutateAsync({
        customerId: customer.id,
        customerName: customer.name,
        accountCategory: formData.accountCategory,
        accountType: formData.accountType,
        routingNumber: formData.routingNumber,
        accountNumber: formData.accountNumber,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone,
        streetAddress: formData.streetAddress.trim() || null,
        city: formData.city.trim() || null,
        state: formData.state || null,
        zipCode: formData.zipCode || null,
      });
      toast.success('Bank details added successfully');
      onClose();
    } catch {
      toast.error('Failed to add bank details');
    }
  };

  return (
    <ModalOverlay title="Bank Details" onClose={onClose} wide>
      <p className="mb-4 text-sm text-muted-foreground">
        Adding bank details for <span className="font-medium text-foreground">{customer.name}</span>
      </p>
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Account Information Section */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground border-b border-border pb-2">
            Account Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">
                Account <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.accountCategory}
                onChange={(e) => update('accountCategory', e.target.value)}
                className={selectClass('accountCategory')}
              >
                <option value="business">Business</option>
                <option value="personal">Personal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium">
                Account Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.accountType}
                onChange={(e) => update('accountType', e.target.value)}
                className={selectClass('accountType')}
              >
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium">
                Routing Number <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.routingNumber}
                onChange={(e) => update('routingNumber', e.target.value)}
                onBlur={() => markTouched('routingNumber')}
                placeholder="9 digit routing number"
                className={inputClass('routingNumber')}
              />
              {touched.routingNumber && fieldErrors.routingNumber && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.routingNumber}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium">
                Account Number <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.accountNumber}
                onChange={(e) => update('accountNumber', e.target.value)}
                onBlur={() => markTouched('accountNumber')}
                placeholder="Account number"
                className={inputClass('accountNumber')}
              />
              {touched.accountNumber && fieldErrors.accountNumber && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.accountNumber}</p>
              )}
            </div>
          </div>
        </div>

        {/* Account Holder Information Section */}
        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground border-b border-border pb-2">
            Account Holder Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.firstName}
                onChange={(e) => update('firstName', e.target.value)}
                onBlur={() => markTouched('firstName')}
                placeholder="First name"
                className={inputClass('firstName')}
              />
              {touched.firstName && fieldErrors.firstName && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.firstName}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.lastName}
                onChange={(e) => update('lastName', e.target.value)}
                onBlur={() => markTouched('lastName')}
                placeholder="Last name"
                className={inputClass('lastName')}
              />
              {touched.lastName && fieldErrors.lastName && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.lastName}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium">Email</label>
              <input
                value={formData.email}
                onChange={(e) => update('email', e.target.value)}
                onBlur={() => markTouched('email')}
                placeholder="email@example.com"
                className={inputClass('email')}
              />
              {touched.email && fieldErrors.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                value={formData.phone}
                onValueChange={(digits) => update('phone', digits)}
                onBlur={() => markTouched('phone')}
                placeholder="123-456-7890"
                className={inputClass('phone')}
              />
              {touched.phone && fieldErrors.phone && <p className="mt-1 text-xs text-red-500">{fieldErrors.phone}</p>}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium">Street Address</label>
              <input
                value={formData.streetAddress}
                onChange={(e) => update('streetAddress', e.target.value)}
                placeholder="123 Main St"
                className={inputClass('streetAddress')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium">City</label>
              <input
                value={formData.city}
                onChange={(e) => update('city', e.target.value)}
                placeholder="City"
                className={inputClass('city')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium">State</label>
              <select
                value={formData.state}
                onChange={(e) => update('state', e.target.value)}
                className={selectClass('state')}
              >
                <option value="">Select State</option>
                {US_STATES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium">Zip Code</label>
              <input
                value={formData.zipCode}
                onChange={(e) => update('zipCode', e.target.value)}
                onBlur={() => markTouched('zipCode')}
                placeholder="12345"
                className={inputClass('zipCode')}
              />
              {touched.zipCode && fieldErrors.zipCode && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.zipCode}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || createBank.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {createBank.isPending ? 'Saving...' : 'Save Bank Details'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Edit Card Details Modal ──

function EditCardDetailsModal({
  customer,
  card,
  onClose,
}: {
  customer: RPCustomer;
  card: RPCustomerCard;
  onClose: () => void;
}) {
  const updateCard = useUpdateRPCustomerCard();
  const [formData, setFormData] = useState({
    cardHolderName: card.cardHolderName || '',
    expirationDate: card.expirationDate || '',
    cardZipCode: card.cardZipCode || '',
    streetAddress: card.streetAddress || '',
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const update = (field: string, value: string) => {
    if (field === 'cardZipCode') {
      value = value.replace(/\D/g, '').slice(0, 5);
    }
    if (field === 'cardHolderName') {
      value = value.replace(/[^a-zA-Z\s]/g, '');
    }
    if (field === 'expirationDate') {
      let v = value.replace(/[^0-9/]/g, '');
      const slashCount = (v.match(/\//g) || []).length;
      if (slashCount > 1) v = v.replace(/\/([^]*)$/, '$1');
      const digits = v.replace(/\//g, '');
      if (digits.length >= 2 && !v.includes('/')) {
        v = digits.slice(0, 2) + '/' + digits.slice(2);
      }
      v = v.slice(0, 5);
      setFormData((prev) => ({ ...prev, expirationDate: v }));
      return;
    }
    if (field === 'streetAddress') {
      value = value.slice(0, 127);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const fieldErrors = useMemo<Record<string, string>>(() => {
    const errors: Record<string, string> = {};
    if (!formData.expirationDate) {
      errors.expirationDate = 'Expiration date is required';
    } else if (!/^\d{2}\/\d{2}$/.test(formData.expirationDate)) {
      errors.expirationDate = 'Expiration date must be in MM/YY format';
    } else {
      const [mm, yy] = formData.expirationDate.split('/').map(Number);
      if (mm < 1 || mm > 12) {
        errors.expirationDate = 'Invalid month';
      } else {
        const now = new Date();
        const currentMonth = now.getMonth() + 1;
        const currentYear = now.getFullYear() % 100;
        if (yy < currentYear || (yy === currentYear && mm < currentMonth)) {
          errors.expirationDate = 'Card has expired';
        }
      }
    }
    if (!formData.cardZipCode) {
      errors.cardZipCode = 'Zip code is required';
    } else if (formData.cardZipCode.length !== 5) {
      errors.cardZipCode = 'Zip code must be 5 digits';
    }
    return errors;
  }, [formData]);

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const inputClass = (field: string) =>
    `mt-1 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${
      touched[field] && fieldErrors[field]
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-input focus:border-primary focus:ring-primary'
    }`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({ expirationDate: true, cardZipCode: true });
    if (!isFormValid) return;
    try {
      await updateCard.mutateAsync({
        customerId: customer.id,
        id: card.id,
        cardHolderName: formData.cardHolderName.trim() || null,
        expirationDate: formData.expirationDate,
        cardZipCode: formData.cardZipCode,
        streetAddress: formData.streetAddress.trim() || null,
      });
      toast.success('Card details updated');
      onClose();
    } catch {
      toast.error('Failed to update card details');
    }
  };

  return (
    <ModalOverlay title="Edit Card Details" onClose={onClose}>
      <p className="mb-4 text-sm text-muted-foreground">
        Editing card •••• {card.cardLastFour} for <span className="font-medium text-foreground">{customer.name}</span>
      </p>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium">Card Number</label>
          <input
            value={`•••• •••• •••• ${card.cardLastFour}`}
            disabled
            className="mt-1 h-9 w-full rounded-lg border border-input bg-muted/50 px-3 text-sm text-muted-foreground"
          />
          <p className="mt-1 text-xs text-muted-foreground">Card number cannot be changed</p>
        </div>
        <div>
          <label className="block text-sm font-medium">Card Holder Name</label>
          <input
            value={formData.cardHolderName}
            onChange={(e) => update('cardHolderName', e.target.value)}
            placeholder="Name on card"
            className={inputClass('cardHolderName')}
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium">
              Expiration Date <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.expirationDate}
              onChange={(e) => update('expirationDate', e.target.value)}
              onBlur={() => markTouched('expirationDate')}
              placeholder="MM/YY"
              className={inputClass('expirationDate')}
            />
            {touched.expirationDate && fieldErrors.expirationDate && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.expirationDate}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium">
              Zip Code <span className="text-red-500">*</span>
            </label>
            <input
              value={formData.cardZipCode}
              onChange={(e) => update('cardZipCode', e.target.value)}
              onBlur={() => markTouched('cardZipCode')}
              placeholder="12345"
              className={inputClass('cardZipCode')}
            />
            {touched.cardZipCode && fieldErrors.cardZipCode && (
              <p className="mt-1 text-xs text-red-500">{fieldErrors.cardZipCode}</p>
            )}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium">Street Address</label>
          <input
            value={formData.streetAddress}
            onChange={(e) => update('streetAddress', e.target.value)}
            placeholder="123 Main St"
            className={inputClass('streetAddress')}
          />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || updateCard.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateCard.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

// ── Edit Bank Details Modal ──

function EditBankDetailsModal({
  customer,
  bank,
  onClose,
}: {
  customer: RPCustomer;
  bank: RPCustomerBank;
  onClose: () => void;
}) {
  const updateBank = useUpdateRPCustomerBank();
  const [formData, setFormData] = useState({
    accountCategory: bank.accountCategory || 'business',
    accountType: bank.accountType || 'checking',
    routingNumber: bank.routingNumber || '',
    accountNumber: bank.accountNumber || '',
    firstName: bank.firstName || '',
    lastName: bank.lastName || '',
    email: bank.email || '',
    phone: bank.phone || '',
    streetAddress: bank.streetAddress || '',
    city: bank.city || '',
    state: bank.state || '',
    zipCode: bank.zipCode || '',
  });
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const markTouched = (field: string) => setTouched((prev) => ({ ...prev, [field]: true }));

  const update = (field: string, value: string) => {
    if (field === 'firstName' || field === 'lastName') {
      value = value.replace(/[^a-zA-Z\s]/g, '');
    }
    if (field === 'routingNumber') {
      value = value.replace(/\D/g, '').slice(0, 9);
    }
    if (field === 'accountNumber') {
      value = value.replace(/\D/g, '').slice(0, 17);
    }
    if (field === 'phone') {
      value = sanitizePhone(value);
    }
    if (field === 'zipCode') {
      value = value.replace(/\D/g, '').slice(0, 5);
    }
    if (field === 'streetAddress') {
      value = value.slice(0, 127);
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const fieldErrors = useMemo<Record<string, string>>(() => {
    const errors: Record<string, string> = {};
    if (!formData.routingNumber) {
      errors.routingNumber = 'Routing number is required';
    } else if (formData.routingNumber.length !== 9) {
      errors.routingNumber = 'Routing number must be 9 digits';
    }
    if (!formData.accountNumber) {
      errors.accountNumber = 'Account number is required';
    } else if (formData.accountNumber.length < 6) {
      errors.accountNumber = 'Account number must be at least 6 digits';
    }
    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    } else if (formData.firstName.trim().length < 2) {
      errors.firstName = 'First name must be at least 2 characters';
    }
    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    } else if (formData.lastName.trim().length < 2) {
      errors.lastName = 'Last name must be at least 2 characters';
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email address';
    }
    {
      const phoneErr = validatePhone(formData.phone, true);
      if (phoneErr) errors.phone = phoneErr;
    }
    if (formData.zipCode && formData.zipCode.length !== 5) {
      errors.zipCode = 'Zip code must be 5 digits';
    }
    return errors;
  }, [formData]);

  const isFormValid = Object.keys(fieldErrors).length === 0;

  const inputClass = (field: string) =>
    `mt-1 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${
      touched[field] && fieldErrors[field]
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-input focus:border-primary focus:ring-primary'
    }`;

  const selectClass = (field: string) =>
    `mt-1 h-9 w-full rounded-lg border px-3 text-sm outline-none focus:ring-1 ${
      touched[field] && fieldErrors[field]
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-input focus:border-primary focus:ring-primary'
    }`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched({
      routingNumber: true,
      accountNumber: true,
      firstName: true,
      lastName: true,
      phone: true,
      email: true,
      zipCode: true,
    });
    if (!isFormValid) return;
    try {
      await updateBank.mutateAsync({
        customerId: customer.id,
        id: bank.id,
        accountCategory: formData.accountCategory,
        accountType: formData.accountType,
        routingNumber: formData.routingNumber,
        accountNumber: formData.accountNumber,
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim() || null,
        phone: formData.phone,
        streetAddress: formData.streetAddress.trim() || null,
        city: formData.city.trim() || null,
        state: formData.state || null,
        zipCode: formData.zipCode || null,
      });
      toast.success('Bank details updated');
      onClose();
    } catch {
      toast.error('Failed to update bank details');
    }
  };

  return (
    <ModalOverlay title="Edit Bank Details" onClose={onClose} wide>
      <p className="mb-4 text-sm text-muted-foreground">
        Editing bank details for <span className="font-medium text-foreground">{customer.name}</span>
      </p>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground border-b border-border pb-2">
            Account Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">
                Account <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.accountCategory}
                onChange={(e) => update('accountCategory', e.target.value)}
                className={selectClass('accountCategory')}
              >
                <option value="business">Business</option>
                <option value="personal">Personal</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium">
                Account Type <span className="text-red-500">*</span>
              </label>
              <select
                value={formData.accountType}
                onChange={(e) => update('accountType', e.target.value)}
                className={selectClass('accountType')}
              >
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium">
                Routing Number <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.routingNumber}
                onChange={(e) => update('routingNumber', e.target.value)}
                onBlur={() => markTouched('routingNumber')}
                placeholder="9 digit routing number"
                className={inputClass('routingNumber')}
              />
              {touched.routingNumber && fieldErrors.routingNumber && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.routingNumber}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium">
                Account Number <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.accountNumber}
                onChange={(e) => update('accountNumber', e.target.value)}
                onBlur={() => markTouched('accountNumber')}
                placeholder="Account number"
                className={inputClass('accountNumber')}
              />
              {touched.accountNumber && fieldErrors.accountNumber && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.accountNumber}</p>
              )}
            </div>
          </div>
        </div>

        <div>
          <h3 className="mb-3 text-sm font-semibold text-foreground border-b border-border pb-2">
            Account Holder Information
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium">
                First Name <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.firstName}
                onChange={(e) => update('firstName', e.target.value)}
                onBlur={() => markTouched('firstName')}
                placeholder="First name"
                className={inputClass('firstName')}
              />
              {touched.firstName && fieldErrors.firstName && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.firstName}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium">
                Last Name <span className="text-red-500">*</span>
              </label>
              <input
                value={formData.lastName}
                onChange={(e) => update('lastName', e.target.value)}
                onBlur={() => markTouched('lastName')}
                placeholder="Last name"
                className={inputClass('lastName')}
              />
              {touched.lastName && fieldErrors.lastName && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.lastName}</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium">Email</label>
              <input
                value={formData.email}
                onChange={(e) => update('email', e.target.value)}
                onBlur={() => markTouched('email')}
                placeholder="email@example.com"
                className={inputClass('email')}
              />
              {touched.email && fieldErrors.email && <p className="mt-1 text-xs text-red-500">{fieldErrors.email}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <PhoneInput
                value={formData.phone}
                onValueChange={(digits) => update('phone', digits)}
                onBlur={() => markTouched('phone')}
                placeholder="123-456-7890"
                className={inputClass('phone')}
              />
              {touched.phone && fieldErrors.phone && <p className="mt-1 text-xs text-red-500">{fieldErrors.phone}</p>}
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium">Street Address</label>
              <input
                value={formData.streetAddress}
                onChange={(e) => update('streetAddress', e.target.value)}
                placeholder="123 Main St"
                className={inputClass('streetAddress')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium">City</label>
              <input
                value={formData.city}
                onChange={(e) => update('city', e.target.value)}
                placeholder="City"
                className={inputClass('city')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium">State</label>
              <select
                value={formData.state}
                onChange={(e) => update('state', e.target.value)}
                className={selectClass('state')}
              >
                <option value="">Select State</option>
                {US_STATES.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium">Zip Code</label>
              <input
                value={formData.zipCode}
                onChange={(e) => update('zipCode', e.target.value)}
                onBlur={() => markTouched('zipCode')}
                placeholder="12345"
                className={inputClass('zipCode')}
              />
              {touched.zipCode && fieldErrors.zipCode && (
                <p className="mt-1 text-xs text-red-500">{fieldErrors.zipCode}</p>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="h-9 rounded-lg border border-border px-4 text-sm font-medium text-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isFormValid || updateBank.isPending}
            className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateBank.isPending ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}
